import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '@/api/axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { TrendingUp, TrendingDown, Calendar, Building2, User, FileText, ChevronDown, ChevronRight, Download, Plus, Trash2, Settings, AlertTriangle, BarChart3, Target, Activity, FileDown, ExternalLink, Check, X as XIcon, Eye, ToggleLeft, ToggleRight, FileSpreadsheet, Layers, Filter, Search, Globe } from 'lucide-react';
import { format, addWeeks, startOfWeek, addMonths, startOfMonth } from 'date-fns';
import { es, enUS, ptBR } from 'date-fns/locale';
import { exportProjections, exportToExcel } from '@/utils/excelExport';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ComposedChart, Area, ReferenceLine } from 'recharts';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { financialTranslations, languages } from '../utils/financialTranslations';

const DIAS_SEMANA = [
  { value: 0, label: 'Domingo' },
  { value: 1, label: 'Lunes' },
  { value: 2, label: 'Martes' },
  { value: 3, label: 'Miércoles' },
  { value: 4, label: 'Jueves' },
  { value: 5, label: 'Viernes' },
  { value: 6, label: 'Sábado' }
];

const CashflowProjections = () => {
  const [language, setLanguage] = useState(localStorage.getItem('taxnfin_language') || 'es');
  const handleLanguageChange = (lang) => { setLanguage(lang); localStorage.setItem('taxnfin_language', lang); };
  const [loading, setLoading] = useState(true);
  const [weeklyData, setWeeklyData] = useState([]);
  const [cxcCxpData, setCxcCxpData] = useState({ cxc: [], cxp: [] });
  const [cfdis, setCfdis] = useState([]);
  const [categories, setCategories] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [viewMode, setViewMode] = useState('weekly');
  const [expandedRows, setExpandedRows] = useState({});
  const [selectedPartyType, setSelectedPartyType] = useState('all');
  const [selectedParty, setSelectedParty] = useState('');
  
  // Translation helper
  const t = financialTranslations[language];
  const dateLocale = language === 'es' ? es : language === 'pt' ? ptBR : enUS;
  
  // Company config
  const [companyConfig, setCompanyConfig] = useState({ inicio_semana: 1 });
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  
  // Guardamos backendWeeks en state para poder re-usarlos al cambiar el filtro de fecha
  const [backendWeeksCache, setBackendWeeksCache] = useState([]);
  
  // User-selected starting date for the 18-week window (overrides auto-detection).
  // Empty string = automatic (default: 4 weeks before today, snapped to weekStart).
  const [customStartDate, setCustomStartDate] = useState(() => {
    try { return localStorage.getItem('cashflow_custom_start_date') || ''; } catch { return ''; }
  });
  
  // Date range filter for week display (empty = default: current week onward)
  const [filterFechaInicio, setFilterFechaInicio] = useState('');
  const [filterFechaFin, setFilterFechaFin] = useState('');

  // Currency selector for projections
  const [selectedCurrency, setSelectedCurrency] = useState('MXN');
  const [fxRates, setFxRates] = useState({ MXN: 1, USD: 17.4545, EUR: 20.4852, GBP: 22.00, JPY: 0.13, CHF: 20.00, CAD: 13.00, CNY: 2.40 });
  
  // Custom concepts state
  const [customConcepts, setCustomConcepts] = useState([]);
  const [conceptDialogOpen, setConceptDialogOpen] = useState(false);
  const [saldoInicialBancos, setSaldoInicialBancos] = useState(0);
  
  // PDF export state
  const [exportingPdf, setExportingPdf] = useState(false);
  const reportRef = useRef(null);
  const chartsRef = useRef(null);
  const tableModelRef = useRef(null);
  const kpiChartsRef = useRef(null);
  // Ref para el patrón "latest ref": openKpiModal lee de aquí en lugar de closures del render
  const kpiStateRef = useRef({ cfoKPIs: null, KPI_DEFS: null, formatCurrency: null });
  // Refs para acceso sincrónico en callbacks (evita valores stale de state)
  const cfdisRef      = useRef([]);
  const categoriesRef = useRef([]);
  const paymentsRef   = useRef([]);
  const [newConcept, setNewConcept] = useState({
    nombre: '',
    tipo: 'egreso',
    monto: '',
    semana: 1,
    mes: 1,
    recurrente: false
  });
  
  // CFO KPIs configuration
  const [umbralMinimoCaja, setUmbralMinimoCaja] = useState(() => {
    const saved = localStorage.getItem('taxnfin_umbral_minimo_caja');
    const parsed = saved !== null ? parseFloat(saved) : NaN;
    return Number.isFinite(parsed) ? parsed : 500000; // Default 500K MXN
  });
  const umbralSaveTimer = useRef(null);
  const updateUmbralMinimoCaja = (value) => {
    const v = parseFloat(value) || 0;
    setUmbralMinimoCaja(v);
    localStorage.setItem('taxnfin_umbral_minimo_caja', String(v));
    // Persistir en servidor por empresa (con debounce) — así no se pierde al
    // recargar ni entre dispositivos/usuarios.
    if (umbralSaveTimer.current) clearTimeout(umbralSaveTimer.current);
    umbralSaveTimer.current = setTimeout(() => {
      api.post('/companies/umbral-minimo-caja', { umbral_minimo_caja: v }).catch(() => {});
    }, 600);
  };
  // Al montar: el valor del servidor (por empresa) manda sobre localStorage
  useEffect(() => {
    api.get('/companies/umbral-minimo-caja')
      .then(r => {
        const v = parseFloat(r.data?.umbral_minimo_caja);
        if (Number.isFinite(v) && v >= 0) {
          setUmbralMinimoCaja(v);
          localStorage.setItem('taxnfin_umbral_minimo_caja', String(v));
        }
      })
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Drill-down dialog state
  const [drillDownOpen, setDrillDownOpen] = useState(false);
  const [drillDownData, setDrillDownData] = useState({
    weekNum: null,
    weekLabel: '',
    dateLabel: '',
    tipo: '', // 'ingreso' | 'egreso'
    categoryName: '',
    subcategoryName: '',
    items: [],
    total: 0
  });

  // View mode toggle: 'categoria' | 'tercero'
  const [tableViewMode, setTableViewMode] = useState('categoria');

  // Modal reclasificación de movimientos sin categoría
  const [clasificarModal, setClasificarModal] = useState({ open: false, item: null, nuevaCategoria: '' });

  // Filters for "Por Proveedor/Cliente" view
  const [partyFilters, setPartyFilters] = useState({
    searchTerm: '',
    tipoTercero: 'todos', // 'todos' | 'cliente' | 'proveedor'
    saldoTipo: 'todos' // 'todos' | 'positivo' | 'negativo'
  });

  // Payments and bank transactions for drill-down
  const [allPayments, setAllPayments] = useState([]);
  const [bankTransactions, setBankTransactions] = useState([]);
  const [reconciliations, setReconciliations] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);

  // KPI Insight modal — status: 'good' | 'warning' | 'danger' | 'neutral'
  const [kpiModal, setKpiModal] = useState({ open: false, name: '', formula: '', description: '', values: {}, insight: '', loading: false, status: 'neutral', kpiKey: '' });

  // Currency list - All available currencies
  const CURRENCIES = [
    { code: 'MXN', name: 'Peso Mexicano', symbol: '$' },
    { code: 'USD', name: 'Dólar USA', symbol: 'US$' },
    { code: 'EUR', name: 'Euro', symbol: '€' },
    { code: 'GBP', name: 'Libra Esterlina', symbol: '£' },
    { code: 'JPY', name: 'Yen Japonés', symbol: '¥' },
    { code: 'CHF', name: 'Franco Suizo', symbol: 'Fr' },
    { code: 'CAD', name: 'Dólar Canadiense', symbol: 'C$' },
    { code: 'CNY', name: 'Yuan Chino', symbol: '¥' },
  ];

  useEffect(() => {
    loadData();
    // Re-run whenever the user-selected start date changes so the 18-week
    // window updates without needing a manual page refresh.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customStartDate]);

  // Inyectar proyecciones manuales CxC/CxP en semanas futuras del modelo
  // CxC/CxP ya se inyectan en processWeeklyData (PASO 3) por matching de fecha.
  // Este useEffect queda desactivado para evitar doble-conteo.
  useEffect(() => {}, [cxcCxpData]); // eslint-disable-line react-hooks/exhaustive-deps

  // Merge backend cashflow/weeks data into local weeks structure.
  // Backend weeks are the authoritative date structure.
  // Local weeks (from processWeeklyData) provide supplementary data: USD trades, local CFDIs.
  // Iterates backendWeeks; finds matching local week by fecha_inicio; merges local data in.
  const applyBackendData = (localWeeks, backendWeeks) => {
    if (!backendWeeks || backendWeeks.length === 0) return localWeeks;

    // Index local weeks by local-time date string (avoids UTC-shift artifacts)
    const localByDate = {};
    localWeeks.forEach(w => {
      const d = w.weekStart instanceof Date ? w.weekStart : new Date(w.weekStart);
      const fi = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
      if (fi) localByDate[fi] = w;
    });

    const buildByCategory = (detalle) => {
      // Claves genéricas del backend → etiqueta neutral (sin mencionar la fuente)
      const GENERIC_CAT = {
        'cobro_alegra': 'Cobros',
        'banco_alegra': 'Pagos',
        'pago_alegra':  'Pagos',
      };
      const bc = {};
      (detalle || []).forEach(item => {
        const rawCat = item.categoria || 'Otros';
        const cat = GENERIC_CAT[rawCat] || rawCat;
        if (!bc[cat]) bc[cat] = { total: 0, bySubcategory: {}, items: [] };
        bc[cat].total += item.monto || 0;
        bc[cat].items.push(item); // preserve ALL fields (uuid, fecha, monto_orig, moneda…) for drill-down modal
        // Analítico: subcategoría = tercero/concepto del movimiento, para que al
        // expandir la categoría se vea el detalle mensual/semanal por contraparte.
        const subKey = item.nombre || item.concepto || item.beneficiario || 'General';
        if (!bc[cat].bySubcategory[subKey]) bc[cat].bySubcategory[subKey] = { total: 0, items: [] };
        bc[cat].bySubcategory[subKey].total += item.monto || 0;
        bc[cat].bySubcategory[subKey].items.push(item);
      });
      return bc;
    };

    // DEBUG — verificar orden y fechas del backend
    console.log('[applyBackendData] Backend fechas (primeras 6):');
    backendWeeks.slice(0, 6).forEach((bw, i) =>
      console.log(`  B${i+1}: ${(bw.fecha_inicio||'').slice(0,10)}  label=${bw.label}`)
    );
    console.log('[applyBackendData] Frontend fechas (primeras 6, antes de fix):');
    localWeeks.slice(0, 6).forEach((w, i) => {
      const d = w.weekStart instanceof Date ? w.weekStart : new Date(w.weekStart);
      const fi = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
      console.log(`  F${i+1}: ${fi}  label=${w.label}`);
    });
    const ascending = backendWeeks.every((bw, i) =>
      i === 0 || (bw.fecha_inicio||'') >= (backendWeeks[i-1].fecha_inicio||'')
    );
    console.log(`[applyBackendData] Backend orden ascendente: ${ascending}`);

    // BACKEND WEEKS AS PRIMARY STRUCTURE
    let matched = 0, missed = 0;
    const result = backendWeeks.map((bw, idx) => {
      const fi = (bw.fecha_inicio || '').slice(0, 10);
      const localWeek = fi ? localByDate[fi] : null;
      if (localWeek) matched++; else missed++;

      // Build weekStart from backend fecha_inicio using local midnight (no UTC shift)
      let weekStart;
      if (fi) {
        const [yr, mo, dy] = fi.split('-').map(Number);
        weekStart = new Date(yr, mo - 1, dy, 0, 0, 0, 0);
      } else {
        weekStart = localWeek?.weekStart || new Date();
      }

      // FIX filtro de rango: weekEnd/isPast/isCurrent deben existir SIEMPRE,
      // no solo cuando hay match local. Sin weekEnd, el filtro "Desde" comparaba
      // `undefined <= fecha` (siempre false) y las semanas sin match local
      // jamás se ocultaban.
      const weekEnd = new Date(weekStart.getTime() + 7 * 86400000);
      const _now = new Date();
      const isPast = weekEnd <= _now;
      const isCurrent = weekStart <= _now && _now < weekEnd;

      const ingBC = buildByCategory(bw.ingresos_detalle);
      const egrBC = buildByCategory(bw.egresos_detalle);

      return {
        ...(localWeek || {}),              // supplement: compraUSD, ventaUSD, local CFDIs, etc.
        weekStart,                          // authoritative date from backend
        weekEnd,                            // SIEMPRE definido (fix filtro de rango)
        isPast,
        isCurrent,
        dataType:     localWeek?.dataType ?? (isPast ? 'real' : isCurrent ? 'actual' : 'proyectado'),
        weekNum:      idx + 1,
        label:        bw.label || `S${idx + 1}`,
        displayLabel: bw.label || `S${idx + 1}`,
        fecha_inicio: fi,
        fecha_fin:    (bw.fecha_fin || '').slice(0, 10),
        backend_saldo_inicial:    bw.saldo_inicial   ?? 0,
        backend_saldo_final:      bw.saldo_final      ?? ((bw.saldo_inicial ?? 0) + (bw.flujo_neto ?? 0)),
        backend_flujo_neto:       bw.flujo_neto       ?? 0,
        backend_ingresos_detalle: bw.ingresos_detalle || [],
        backend_egresos_detalle:  bw.egresos_detalle  || [],
        saldo_anclado:            bw.saldo_anclado    ?? false,
        traspasos_ingreso:        bw.traspasos_ingreso ?? 0,
        traspasos_egreso:         bw.traspasos_egreso  ?? 0,
        traspasos_detalle:        bw.traspasos_detalle ?? [],
        ingresos: {
          total:      bw.total_ingresos ?? (localWeek?.ingresos?.total ?? 0),
          byCategory: Object.keys(ingBC).length > 0 ? ingBC : (localWeek?.ingresos?.byCategory ?? {}),
        },
        egresos: {
          total:      bw.total_egresos ?? (localWeek?.egresos?.total ?? 0),
          byCategory: Object.keys(egrBC).length > 0 ? egrBC : (localWeek?.egresos?.byCategory ?? {}),
        },
        hasRealData: (bw.total_ingresos > 0 || bw.total_egresos > 0) || (localWeek?.hasRealData ?? false),
      };
    });

    console.log(`[applyBackendData] resultado: ${matched} semanas con datos locales / ${missed} solo-backend (total: ${backendWeeks.length})`);
    return result;
  };

  // ── Reclasificar movimiento sin categoría ────────────────────────────────
  const handleCategorizar = async () => {
    const { item, nuevaCategoria } = clasificarModal;
    if (!item || !nuevaCategoria.trim()) return;
    try {
      await api.patch(`/bank-transactions/${item.id}/categorize`, {
        category_name: nuevaCategoria.trim(),
        alegra_id: item.alegra_id || '',
      });
      toast.success(`Reclasificado como "${nuevaCategoria.trim()}"`);
      setClasificarModal({ open: false, item: null, nuevaCategoria: '' });
      loadData(); // recargar para que el cashflow refleje la nueva categoría
    } catch (err) {
      toast.error('Error al reclasificar: ' + (err?.response?.data?.detail || err.message));
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [cfdiRes, catRes, custRes, vendRes, conceptsRes, fxRes, paymentsRes, bankTxnsRes, reconRes, bankAccountsRes, backendWeeksRes] = await Promise.all([
        api.get('/cfdi?limit=500'),
        api.get('/cashflow-sync/categories'),
        api.get('/customers'),
        api.get('/vendors'),
        api.get('/manual-projections'),
        api.get('/fx-rates/latest'),
        api.get('/payments?limit=1000'),
        api.get('/bank-transactions?limit=500'),
        api.get('/reconciliations'),
        api.get('/bank-accounts'),
        api.get('/cashflow/weeks?num_weeks=52').catch(() => ({ data: [] }))
      ]);

      // Obtener saldo inicial bancario con la fecha de S1 del backend
      // para que refleje el saldo histórico verificado (ancla de fin de mes anterior)
      // en lugar del saldo actual (que sería el de mayo 2026).
      const backendWeeksData = backendWeeksRes.data || [];
      const fechaS1 = backendWeeksData[0]?.fecha_inicio
        ? backendWeeksData[0].fecha_inicio.slice(0, 10)
        : null;
      // Usar fecha_fin de S1 para capturar la ancla del cierre del mes previo.
      // "S1 - 1 día" fallaba cuando S1 cae dentro del mismo mes que la ancla
      // (ej. S1=Dec 29, ancla=Dec 31 → Dec 28 < Dec 31 → ancla no encontrada).
      // fecha_fin de S1 siempre cae después de la ancla mensual anterior.
      const fechaFinS1 = backendWeeksData[0]?.fecha_fin
        ? backendWeeksData[0].fecha_fin.slice(0, 10)
        : null;
      const fechaSummary = fechaFinS1 || fechaS1;
      const bankSummaryRes = await api.get(`/bank-accounts/summary${fechaSummary ? `?fecha=${fechaSummary}` : ''}`);
      console.log('[saldo inicial] fechaSummary=', fechaSummary, 'total_mxn=', bankSummaryRes.data?.total_mxn);
      
      setCfdis(cfdiRes.data);
      setCategories(catRes.data);
      setCustomers(custRes.data);
      setVendors(vendRes.data);
      // Actualizar refs para acceso sincrónico en callbacks
      cfdisRef.current      = cfdiRes.data || [];
      categoriesRef.current = catRes.data  || [];
      
      // Store payments and bank transactions for drill-down
      setAllPayments(paymentsRes.data || []);
      setBankTransactions(bankTxnsRes.data || []);
      setReconciliations(reconRes.data || []);
      setBankAccounts(bankAccountsRes.data || []);
      
      // Get initial bank balance
      const totalBancosMXN = bankSummaryRes.data?.total_mxn || 0;
      setSaldoInicialBancos(totalBancosMXN);
      console.log('[CashFlow base bancaria]', {
        total_mxn: totalBancosMXN,
        por_banco: bankSummaryRes.data?.por_banco,
        por_moneda: bankSummaryRes.data?.por_moneda,
      });
      
      // Load FX rates — /fx-rates/latest returns {USD: {tipo_cambio, ...}} not {USD: number}
      const _rawRates = fxRes.data || {};
      const loadedRates = {};
      Object.entries(_rawRates).forEach(([moneda, val]) => {
        if (typeof val === 'number') { loadedRates[moneda] = val; }
        else if (val && typeof val === 'object') { loadedRates[moneda] = val.tipo_cambio || val.tasa || 0; }
      });
      if (!loadedRates.USD || loadedRates.USD <= 0) loadedRates.USD = 17.5;
      if (!loadedRates.EUR || loadedRates.EUR <= 0) loadedRates.EUR = 20.0;
      setFxRates(prev => ({ ...prev, ...loadedRates, MXN: 1 }));
      
      // Load custom concepts from backend
      setCustomConcepts(conceptsRes.data || []);
      
      // Build set of truly conciliated bank transaction IDs
      const bankTxns = bankTxnsRes.data || [];
      const conciliatedBankTxnIds = new Set(
        bankTxns.filter(t => t.conciliado === true).map(t => t.id)
      );
      
      // Filter payments: only include those with TRULY conciliated bank transactions
      // A payment is valid if:
      // 1. It has no bank_transaction_id (manual payment without reconciliation)
      // 2. OR its bank_transaction_id is in the set of conciliated transactions
      const rawPayments = paymentsRes.data || [];
      const validPayments = rawPayments.filter(p => {
        if (!p.bank_transaction_id) return true;
        return conciliatedBankTxnIds.has(p.bank_transaction_id);
      });
      
      console.log(`=== FILTRO DE PAGOS ===`);
      console.log(`Total pagos: ${rawPayments.length}`);
      console.log(`Pagos válidos (conciliados): ${validPayments.length}`);
      console.log(`Pagos excluidos (txn pendiente): ${rawPayments.length - validPayments.length}`);
      
      // Guardar en state para que el filtro de fecha pueda reusarlos
      setAllPayments(rawPayments);
      paymentsRef.current = validPayments; // ref con pagos válidos (conciliados)
      
      const categoriesLoaded = catRes.data || [];
      
      // Get company config for week start day.
      // The active company is stored as JSON under 'selectedCompany' (set by App.js).
      // We also fall back to the user's company_id from the stored auth user.
      const getActiveCompanyId = () => {
        try {
          const sel = sessionStorage.getItem('selectedCompany') || localStorage.getItem('selectedCompany');
          if (sel) {
            const parsed = JSON.parse(sel);
            if (parsed?.id) return parsed.id;
          }
        } catch {/* ignore */}
        try {
          const u = sessionStorage.getItem('user') || localStorage.getItem('user');
          if (u) {
            const parsed = JSON.parse(u);
            if (parsed?.company_id) return parsed.company_id;
          }
        } catch {/* ignore */}
        return null;
      };
      
      const backendWeeks = backendWeeksData;
      setBackendWeeksCache(backendWeeks); // ← guardar para reuso al cambiar filtro
      const companyId = getActiveCompanyId();
      if (companyId) {
        try {
          const compRes = await api.get(`/companies/${companyId}`);
          const weekStart = compRes.data?.inicio_semana ?? 1;
          setCompanyConfig({ ...compRes.data, inicio_semana: weekStart });
          // Use backend S1 fecha_inicio as startWeek anchor so all 52 weeks align.
          // customStartDate (user override) still takes priority when set.
          const backendStart = (backendWeeks[0]?.fecha_inicio || '').slice(0, 10);
          const effectiveStart = customStartDate || backendStart;
          const weeks = processWeeklyData(cfdiRes.data, categoriesLoaded, weekStart, loadedRates, validPayments, effectiveStart);
          setWeeklyData(applyBackendData(weeks, backendWeeks));
        } catch {
          const backendStart = (backendWeeks[0]?.fecha_inicio || '').slice(0, 10);
          const effectiveStart = customStartDate || backendStart;
          const weeks = processWeeklyData(cfdiRes.data, categoriesLoaded, 1, loadedRates, validPayments, effectiveStart);
          setWeeklyData(applyBackendData(weeks, backendWeeks));
        }
      } else {
        const backendStart = (backendWeeks[0]?.fecha_inicio || '').slice(0, 10);
        const effectiveStart = customStartDate || backendStart;
        const weeks = processWeeklyData(cfdiRes.data, categoriesLoaded, 1, loadedRates, validPayments, effectiveStart);
        setWeeklyData(applyBackendData(weeks, backendWeeks));
      }

      // Vista mensual se deriva de weeklyTotals en render — no requiere llamada separada
    } catch (error) {
      console.error('Error loading cashflow data:', error);
      toast.error(t?.errorLoadingData || 'Error loading data');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveCustomStartDate = (newDate) => {
    try {
      if (newDate) {
        localStorage.setItem('cashflow_custom_start_date', newDate);
      } else {
        localStorage.removeItem('cashflow_custom_start_date');
      }
    } catch {/* ignore */}
    // setState triggers the useEffect below which calls loadData with the fresh value.
    setCustomStartDate(newDate);
    if (newDate) {
      toast.success(`Inicio del flujo establecido al ${newDate}`);
    } else {
      toast.success('Inicio del flujo restaurado a automático');
    }
  };

  const handleSaveWeekStart = async (newWeekStart) => {
    try {
      let companyId = null;
      try {
        const sel = sessionStorage.getItem('selectedCompany') || localStorage.getItem('selectedCompany');
        if (sel) companyId = JSON.parse(sel)?.id || null;
      } catch {/* ignore */}
      if (!companyId) {
        try {
          const u = sessionStorage.getItem('user') || localStorage.getItem('user');
          if (u) companyId = JSON.parse(u)?.company_id || null;
        } catch {/* ignore */}
      }
      if (!companyId) {
        toast.error('No se encontró la empresa activa');
        return;
      }
      await api.put(`/companies/${companyId}`, { inicio_semana: newWeekStart });
      setCompanyConfig(prev => ({ ...prev, inicio_semana: newWeekStart }));
      try {
        const sel = sessionStorage.getItem('selectedCompany') || localStorage.getItem('selectedCompany');
        if (sel) {
          const parsed = JSON.parse(sel);
          parsed.inicio_semana = newWeekStart;
          sessionStorage.setItem('selectedCompany', JSON.stringify(parsed));
          localStorage.setItem('selectedCompany', JSON.stringify(parsed));
        }
      } catch {/* ignore */}
      // Reload data to reprocess with new week start
      loadData();
      setConfigDialogOpen(false);
      toast.success(`Inicio de semana cambiado a ${DIAS_SEMANA.find(d => d.value === newWeekStart)?.label}`);
    } catch (error) {
      toast.error('Error al guardar configuración');
    }
  };

  // Helper function to convert amount to MXN
  const convertToMXN = (amount, currency, rates = {}) => {
    if (!amount) return 0;
    if (currency === 'MXN' || !currency) return amount;
    // Try rates passed in, then fxRates state, then default
    const rate = rates[currency] || fxRates[currency] || 17.4545;
    return amount * rate;
  };

  const processWeeklyData = (cfdisData, categoriesData, weekStartDay = 1, rates = {}, payments = [], customStart = '') => {
    // =====================================================================
    // NUEVA LÓGICA: ÚNICA FUENTE DE VERDAD
    // - Semanas pasadas/actuales: SOLO datos de Cobranza y Pagos
    // - Semanas futuras: CFDIs pendientes (proyecciones)
    // - TOTAL INGRESOS = Suma exacta de sublíneas por categoría
    // =====================================================================
    
    const effectiveRates = { MXN: 1, USD: 17.599, EUR: 20.4852, ...fxRates, ...rates };
    
    // Returns the start of the week for `date` according to `weekStartDay`.
    // weekStartDay: 0=Sunday, 1=Monday, 2=Tuesday, ..., 6=Saturday
    const getWeekStart = (date, startDay = 1) => {
      const d = new Date(date);
      d.setHours(0, 0, 0, 0);
      const day = d.getDay(); // 0 (Sun) - 6 (Sat)
      let diff = day - startDay;
      if (diff < 0) diff += 7;
      d.setDate(d.getDate() - diff);
      return d;
    };
    
    const today = new Date();
    const currentWeekStart = getWeekStart(today, weekStartDay);
    
    // Fiscal year anchor for LABELS: S1 = Monday on or before Jan 1 of current year.
    // Mirrors backend: model_start = date(year,1,1) - timedelta(days=weekday).
    // weekStartDay 0=Sunday, 1=Monday (default).
    const _yearStart = new Date(today.getFullYear(), 0, 1);
    const _daysOffset = weekStartDay === 0
      ? _yearStart.getDay()              // Sunday start: JS getDay() directly
      : (_yearStart.getDay() + 6) % 7;  // Monday start: convert to Python weekday
    const FISCAL_YEAR_START = new Date(_yearStart.getTime() - _daysOffset * 86400000);

    // Find earliest payment date to anchor the historical display window
    let earliestDate = null;
    payments.filter(p => p.estatus === 'completado' || p.estatus === 'pagado' || p.status === 'pagado' || p.es_real === true).forEach(p => {
      const fecha = p.fecha_pago;
      if (fecha) {
        const d = new Date(fecha);
        if (!earliestDate || d < earliestDate) earliestDate = d;
      }
    });

    const fourWeeksAgo = addWeeks(currentWeekStart, -17);

    // Window start priority: 1) explicit customStart, 2) earliest payment, 3) 17 weeks ago
    let startWeek;
    if (customStart) {
      // Parse as local date — bare 'YYYY-MM-DD' strings are treated as UTC in JS,
      // which shifts one day back in UTC- timezones (e.g. Mexico City UTC-6),
      // causing getWeekStart to land on the wrong Monday.
      const parts = String(customStart).split('-').map(Number);
      const parsed = parts.length === 3
        ? new Date(parts[0], parts[1] - 1, parts[2], 0, 0, 0, 0)
        : new Date(customStart);
      if (!isNaN(parsed.getTime())) {
        startWeek = getWeekStart(parsed, weekStartDay);
      }
    }
    if (!startWeek) {
      startWeek = earliestDate
        ? getWeekStart(earliestDate, weekStartDay)
        : fourWeeksAgo;
    }
    
    // Generate 52 weeks: historical (Real) + 1 current (Actual) + up to 35 projected forward
    const weeks = [];
    for (let i = 0; i < 52; i++) {
      const weekStart = addWeeks(startWeek, i);
      const weekEnd = addWeeks(weekStart, 1);
      const isPast = weekEnd <= today;
      const isCurrent = weekStart <= today && today < weekEnd;
      
      // Determine data source type for CFO KPIs
      // S1-S4: Historical (Real), S5: Current (Actual), S6-S18: Projected (Proyectado)
      let dataType = 'proyectado';
      if (isPast) dataType = 'real';
      else if (isCurrent) dataType = 'actual';
      
      const fiscalDiff = Math.round(
        (weekStart.getTime() - FISCAL_YEAR_START.getTime()) / (7 * 24 * 60 * 60 * 1000)
      );
      const displayLabel = fiscalDiff >= 0
        ? `S${fiscalDiff + 1}`
        : `S${53 + fiscalDiff}/${String(weekStart.getFullYear()).slice(-2)}`;

      weeks.push({
        weekNum: i + 1,
        weekStart,
        weekEnd,
        label: `S${i + 1}`,
        displayLabel,
        dateLabel: format(weekStart, 'dd MMM', { locale: es }),
        isPast,
        isCurrent,
        dataType, // 'real' | 'actual' | 'proyectado'
        // INGRESOS structure by category/subcategory
        ingresos: { total: 0, byCategory: {} },
        // EGRESOS structure by category/subcategory
        egresos: { total: 0, byCategory: {} },
        // USD operations tracked separately
        compraUSD: 0,
        ventaUSD: 0,
        // Source flags
        hasRealData: false
      });
    }
    
    // Build category/subcategory lookup maps
    const categoryMap = {};
    const subcategoryMap = {};
    categoriesData.forEach(cat => {
      categoryMap[cat.id] = cat;
      if (cat.code) categoryMap[cat.code] = cat; // cashflow-sync categories use 'code' as id
      cat.subcategorias?.forEach(sub => {
        subcategoryMap[sub.id] = { ...sub, categoryName: cat.nombre, categoryId: cat.id };
      });
    });
    
    // Find USD operation categories
    const compraUSDCategory = categoriesData.find(c => c.nombre?.toLowerCase().includes('compra de usd') || c.nombre?.toLowerCase().includes('compra usd'));
    const ventaUSDCategory = categoriesData.find(c => c.nombre?.toLowerCase().includes('venta de usd') || c.nombre?.toLowerCase().includes('venta usd'));
    const compraUSDId = compraUSDCategory?.id;
    const ventaUSDId = ventaUSDCategory?.id;
    
    // Track processed bank transactions to avoid duplicates
    const processedBankTxns = new Set();
    
    // =====================================================================
    // PASO 0: Pre-procesar operaciones de divisas para calcular TC implícito
    // El TC implícito = MXN recibidos (Venta USD) / USD pagados (Compra USD)
    // Esto hace que el efecto neto de las operaciones de cambio sea 0
    // =====================================================================
    const weekCurrencyOps = {}; // { weekIdx: { ventaMXN: 0, compraUSD: 0 } }
    
    payments.forEach(payment => {
      if (payment.estatus !== 'completado' && payment.status !== 'pagado') return;
      if (!payment.fecha_pago) return;
      
      const paymentDate = new Date(payment.fecha_pago);
      if (isNaN(paymentDate.getTime())) return;
      
      const weekIdx = weeks.findIndex(w => paymentDate >= w.weekStart && paymentDate < w.weekEnd);
      if (weekIdx === -1) return;
      
      const week = weeks[weekIdx];
      if (!week.isPast && !week.isCurrent) return;
      
      const isCompraUSD = payment.category_id && compraUSDId && payment.category_id === compraUSDId;
      const isVentaUSD = payment.category_id && ventaUSDId && payment.category_id === ventaUSDId;
      
      if (!weekCurrencyOps[weekIdx]) {
        weekCurrencyOps[weekIdx] = { ventaMXN: 0, compraUSD: 0 };
      }
      
      if (isVentaUSD) {
        // Venta de USD: el monto es en MXN (entrada de pesos)
        weekCurrencyOps[weekIdx].ventaMXN += payment.monto;
      }
      if (isCompraUSD && payment.moneda === 'USD') {
        // Compra de USD: el monto es en USD (dólares que compramos)
        weekCurrencyOps[weekIdx].compraUSD += payment.monto;
      }
    });
    
    // Calcular TC implícito por semana
    const weekImplicitTC = {};
    Object.entries(weekCurrencyOps).forEach(([weekIdx, ops]) => {
      if (ops.compraUSD > 0 && ops.ventaMXN > 0) {
        // TC implícito = MXN recibidos / USD pagados
        weekImplicitTC[weekIdx] = ops.ventaMXN / ops.compraUSD;
      }
    });
    
    // =====================================================================
    // PASO 1: Procesar Cobranza y Pagos (DATOS REALES para semanas pasadas)
    // =====================================================================
    payments.forEach(payment => {
      if (payment.estatus !== 'completado' && payment.status !== 'pagado') return;
      if (!payment.fecha_pago) return;
      
      // Deduplicate by bank_transaction_id
      const bankTxnId = payment.bank_transaction_id;
      if (bankTxnId) {
        if (processedBankTxns.has(bankTxnId)) return;
        processedBankTxns.add(bankTxnId);
      }
      
      const paymentDate = new Date(payment.fecha_pago);
      if (isNaN(paymentDate.getTime())) return;
      
      const weekIdx = weeks.findIndex(w => paymentDate >= w.weekStart && paymentDate < w.weekEnd);
      if (weekIdx === -1) return;
      
      const week = weeks[weekIdx];
      if (!week.isPast && !week.isCurrent) return; // Only process real data for past/current weeks
      
      week.hasRealData = true;
      
      // Get category and subcategory names from payment's inherited data
      const category = categoryMap[payment.category_id];
      const categoryName = category?.nombre || 'Sin categoría';
      const subcategoryInfo = subcategoryMap[payment.subcategory_id];
      const subcategoryName = subcategoryInfo?.nombre || null;
      
      // Check if USD operation (only if category_id is not null)
      const isCompraUSD = payment.category_id && compraUSDId && payment.category_id === compraUSDId;
      const isVentaUSD = payment.category_id && ventaUSDId && payment.category_id === ventaUSDId;
      
      if (isVentaUSD) {
        // Venta de USD: el monto ya está en MXN
        week.ventaUSD += payment.monto;
        return; // Don't add to regular ingresos/egresos
      }
      
      if (isCompraUSD) {
        // Compra de USD: usar TC implícito para que el efecto neto sea 0
        // Si hay TC implícito, usarlo; si no, usar el TC del pago o el estándar
        const implicitTC = weekImplicitTC[weekIdx];
        let montoMXN;
        
        if (implicitTC && payment.moneda === 'USD') {
          // Usar TC implícito para efecto neto = 0
          montoMXN = payment.monto * implicitTC;
        } else if (payment.moneda === 'USD') {
          // Usar TC histórico del pago o el estándar
          const tc = payment.tipo_cambio_historico || effectiveRates.USD || 17.5;
          montoMXN = payment.monto * tc;
        } else {
          montoMXN = payment.monto;
        }
        
        week.compraUSD += montoMXN;
        return; // Don't add to regular ingresos/egresos
      }
      
      const montoMXN = convertToMXN(payment.monto, payment.moneda, effectiveRates);
      
      // Determine section (ingresos or egresos)
      const section = payment.tipo === 'cobro' ? week.ingresos : week.egresos;
      
      // Add to category total
      if (!section.byCategory[categoryName]) {
        section.byCategory[categoryName] = { total: 0, bySubcategory: {}, items: [] };
      }
      section.byCategory[categoryName].total += montoMXN;
      section.byCategory[categoryName].items.push({
        id: payment.id,
        monto: montoMXN,
        concepto: payment.concepto,
        beneficiario: payment.beneficiario,
        uuid: payment.cfdi_uuid,
        bankAccountId: payment.bank_account_id,
        source: 'payment'
      });
      
      // Add to subcategory
      const subKey = subcategoryName || 'General';
      if (!section.byCategory[categoryName].bySubcategory[subKey]) {
        section.byCategory[categoryName].bySubcategory[subKey] = { total: 0, items: [] };
      }
      section.byCategory[categoryName].bySubcategory[subKey].total += montoMXN;
      section.byCategory[categoryName].bySubcategory[subKey].items.push({
        id: payment.id,
        monto: montoMXN,
        beneficiario: payment.beneficiario,
        bankAccountId: payment.bank_account_id,
        source: 'payment'
      });
      
      // Add to section total
      section.total += montoMXN;
    });

    // =====================================================================
    // PASO 1.5: Proyecciones manuales de Cobranza y Pagos (es_real === false)
    // Registradas en PaymentsModule con toggle "Proyección" y estatus 'pendiente'.
    // Usan fecha_vencimiento (no fecha_pago) y solo aplican a semanas futuras/actual.
    // =====================================================================
    payments.forEach(payment => {
      if (payment.es_real !== false) return;
      if (payment.estatus !== 'pendiente' && payment.estatus !== 'proyectado') return;
      if (!payment.fecha_vencimiento) return;

      const vencDate = new Date(payment.fecha_vencimiento);
      if (isNaN(vencDate.getTime())) return;

      const weekIdx = weeks.findIndex(w => vencDate >= w.weekStart && vencDate < w.weekEnd);
      if (weekIdx === -1) return;

      const week = weeks[weekIdx];
      if (week.isPast) return; // Solo semanas actual y futuras

      const category = categoryMap[payment.category_id];
      const categoryName = category?.nombre || 'Sin categoría';
      const subcategoryInfo = subcategoryMap[payment.subcategory_id];
      const subcategoryName = subcategoryInfo?.nombre || null;

      const montoMXN = convertToMXN(Math.abs(payment.monto_mxn ?? payment.monto), 'MXN', effectiveRates);
      if (montoMXN <= 0) return;

      const section = payment.tipo === 'cobro' ? week.ingresos : week.egresos;

      if (!section.byCategory[categoryName]) {
        section.byCategory[categoryName] = { total: 0, bySubcategory: {}, items: [] };
      }
      section.byCategory[categoryName].total += montoMXN;
      section.byCategory[categoryName].items.push({
        id: payment.id,
        monto: montoMXN,
        concepto: payment.concepto,
        beneficiario: payment.beneficiario,
        uuid: payment.cfdi_uuid,
        bankAccountId: payment.bank_account_id,
        source: 'payment_proyeccion'
      });

      const subKey = subcategoryName || 'General';
      if (!section.byCategory[categoryName].bySubcategory[subKey]) {
        section.byCategory[categoryName].bySubcategory[subKey] = { total: 0, items: [] };
      }
      section.byCategory[categoryName].bySubcategory[subKey].total += montoMXN;
      section.byCategory[categoryName].bySubcategory[subKey].items.push({
        id: payment.id,
        monto: montoMXN,
        concepto: payment.concepto,
        beneficiario: payment.beneficiario,
        bankAccountId: payment.bank_account_id,
        source: 'payment_proyeccion'
      });

      section.total += montoMXN;
    });

    return weeks;
  };


  const toggleRow = (key) => {
    setExpandedRows(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const formatCurrency = (amount) => {
    const converted = convertToCurrency(amount || 0);
    const currencyInfo = CURRENCIES.find(c => c.code === selectedCurrency);
    const symbol = currencyInfo?.symbol || '$';
    return `${symbol}${converted.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Add custom concept - saves to backend
  const handleAddConcept = async () => {
    if (!newConcept.nombre || !newConcept.monto) {
      toast.error('Completa nombre y monto');
      return;
    }
    
    try {
      const conceptData = {
        nombre: newConcept.nombre,
        tipo: newConcept.tipo,
        monto: parseFloat(newConcept.monto),
        semana: viewMode === 'weekly' ? parseInt(newConcept.semana) : null,
        mes: viewMode === 'monthly' ? parseInt(newConcept.mes) : null,
        recurrente: newConcept.recurrente
      };
      
      const response = await api.post('/manual-projections', conceptData);
      setCustomConcepts([...customConcepts, response.data]);
      setNewConcept({ nombre: '', tipo: 'egreso', monto: '', semana: 1, mes: 1, recurrente: false });
      setConceptDialogOpen(false);
      toast.success('Concepto agregado');
    } catch (error) {
      toast.error('Error al guardar concepto');
    }
  };

  const handleDeleteConcept = async (id) => {
    try {
      await api.delete(`/manual-projections/${id}`);
      setCustomConcepts(customConcepts.filter(c => c.id !== id));
      toast.success('Concepto eliminado');
    } catch (error) {
      toast.error('Error al eliminar concepto');
    }
  };

  // Export projections to Excel using XLSX
  const exportProjectionsToExcel = () => {
    const totals = calculateRunningTotals();
    
    if (weeklyData.length === 0) {
      toast.error('No hay datos para exportar');
      return;
    }
    
    try {
      // Use the utility function from excelExport
      const success = exportProjections(totals, saldoInicialBancos, selectedCurrency, fxRates[selectedCurrency] || 1);
      if (success) {
        toast.success(`Proyección exportada a Excel en ${selectedCurrency}`);
      } else {
        toast.error('Error al exportar');
      }
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Error al exportar: ' + (error.message || 'Error desconocido'));
    }
  };

  // Convert amount from MXN to selected currency
  const convertToCurrency = (amountMXN) => {
    if (selectedCurrency === 'MXN') return amountMXN;
    const rate = fxRates[selectedCurrency] || 1;
    return amountMXN / rate; // Divide to convert FROM MXN TO target currency
  };

  // Derive unique clientes and proveedores from allPayments
  // (customers/vendors collections are empty — Contalink stores names in payments.beneficiario)
  const paymentClientes = React.useMemo(() => {
    const map = {};
    allPayments.forEach(p => {
      if ((p.estatus !== 'completado' && p.status !== 'pagado') || p.tipo !== 'cobro') return;
      const nombre = p.beneficiario || 'Sin asignar';
      if (!map[nombre]) map[nombre] = { id: nombre, nombre, rfc: '', total: 0, count: 0 };
      map[nombre].total += p.monto || 0;
      map[nombre].count += 1;
    });
    return Object.values(map).sort((a, b) => b.total - a.total);
  }, [allPayments]);

  const paymentProveedores = React.useMemo(() => {
    const map = {};
    allPayments.forEach(p => {
      if ((p.estatus !== 'completado' && p.status !== 'pagado') || p.tipo !== 'pago') return;
      const nombre = p.beneficiario || 'Sin asignar';
      if (!map[nombre]) map[nombre] = { id: nombre, nombre, rfc: '', total: 0, count: 0 };
      map[nombre].total += p.monto || 0;
      map[nombre].count += 1;
    });
    return Object.values(map).sort((a, b) => b.total - a.total);
  }, [allPayments]);

  // Get payments for selected party (cfdis collection is empty; data lives in allPayments)
  const getPartyCfdis = () => {
    if (!selectedParty) return [];
    return allPayments.filter(p => {
      if (p.estatus !== 'completado' && p.status !== 'pagado') return false;
      if (selectedPartyType === 'customer') {
        if (p.tipo !== 'cobro') return false;
        return p.beneficiario === selectedParty || p.customer_id === selectedParty;
      } else if (selectedPartyType === 'vendor') {
        if (p.tipo !== 'pago') return false;
        return p.beneficiario === selectedParty || p.vendor_id === selectedParty;
      }
      return false;
    });
  };

  // Calculate running totals including custom concepts
  // NUEVA LÓGICA SIMPLIFICADA:
  // - Usa directamente ingresos.total y egresos.total de processWeeklyData
  // - TOTAL = Suma exacta de categorías (ya calculado correctamente)
  const calculateRunningTotals = () => {
    // FIX: usar backend_saldo_inicial de S1 como punto de partida.
    // El endpoint /bank-accounts/summary?fecha=... usa saldo_inicial estático de la cuenta
    // (sin bank_account_history), lo que produce un saldo inicial incorrecto cuando
    // no hay registros históricos para ese período.
    let saldoInicial = (weeklyData.length > 0 && weeklyData[0].backend_saldo_inicial != null)
      ? weeklyData[0].backend_saldo_inicial
      : saldoInicialBancos;
    const totals = [];

    weeklyData.forEach((week, idx) => {
      const weekCustomIng = customConcepts.filter(c => c.tipo === 'ingreso' && (c.semana === idx + 1 || c.recurrente));
      const weekCustomEgr = customConcepts.filter(c => c.tipo === 'egreso' && (c.semana === idx + 1 || c.recurrente));

      const customIngresos = weekCustomIng.reduce((sum, c) => sum + c.monto, 0);
      const customEgresos  = weekCustomEgr.reduce((sum, c) => sum + c.monto, 0);

      // Enriquecer byCategory con custom concepts como ítems sintéticos,
      // igual que PASO 3 hace con CxC/CxP — así los tres niveles suman lo mismo.
      const ingByCategory = { ...week.ingresos.byCategory };
      weekCustomIng.forEach(c => {
        ingByCategory[c.nombre] = {
          total:         (ingByCategory[c.nombre]?.total || 0) + c.monto,
          bySubcategory: { ...(ingByCategory[c.nombre]?.bySubcategory || {}) },
          items: [
            ...(ingByCategory[c.nombre]?.items || []),
            { id: `custom-ing-${c.id || c.nombre}-${idx}`, monto: c.monto, concepto: c.nombre, beneficiario: c.nombre, source: 'custom_concept' }
          ]
        };
      });

      const egrByCategory = { ...week.egresos.byCategory };
      weekCustomEgr.forEach(c => {
        egrByCategory[c.nombre] = {
          total:         (egrByCategory[c.nombre]?.total || 0) + c.monto,
          bySubcategory: { ...(egrByCategory[c.nombre]?.bySubcategory || {}) },
          items: [
            ...(egrByCategory[c.nombre]?.items || []),
            { id: `custom-egr-${c.id || c.nombre}-${idx}`, monto: c.monto, concepto: c.nombre, beneficiario: c.nombre, source: 'custom_concept' }
          ]
        };
      });

      const compraUSD = week.compraUSD || 0;
      const ventaUSD  = week.ventaUSD  || 0;
      const totalIngresos = (week.ingresos.total || 0) + customIngresos;
      const totalEgresos  = (week.egresos.total  || 0) + customEgresos;
      const flujoNetoOperativo = totalIngresos - totalEgresos;
      const flujoDivisas = ventaUSD - compraUSD;
      const flujoNeto    = flujoNetoOperativo + flujoDivisas;

      // Fix F: Rolling con piso en backend_saldo_final cuando la fórmula da negativo.
      // SI(n+1) = SF(n) siempre — sin resets, sin saltos, continuidad garantizada.
      // Cuando bank_transactions tiene data incompleta (ingresos faltantes en Alegra)
      // el rolling puro daría negativo. En ese caso SF usa el saldo verificado del backend
      // como piso — pero el SI nunca salta, mantiene la cadena.
      const rawSF = saldoInicial + flujoNeto;
      const saldoFinal = rawSF >= 0
        ? rawSF
        : Math.max(rawSF, week.backend_saldo_final ?? 0);

      totals.push({
        ...week,
        ingresos: { ...week.ingresos, byCategory: ingByCategory, total: totalIngresos, custom: customIngresos },
        egresos:  { ...week.egresos,  byCategory: egrByCategory, total: totalEgresos,  custom: customEgresos  },
        compraUSD, ventaUSD, flujoDivisas, saldoInicial, flujoNeto, saldoFinal
      });

      saldoInicial = saldoFinal;
    });

    // Exponer diagnóstico como variable global para debug rápido en consola
    // Uso: window.__cf.filter(w => w.SF < 0)  o  copy(JSON.stringify(window.__cf))
    window.__cf = totals.map(w => ({
      s: w.label || w.displayLabel,
      fi: (w.fecha_inicio || '').slice(5),
      ff: (w.fecha_fin   || '').slice(5),
      bk_si: Math.round(w.backend_saldo_inicial ?? 0),
      bk_sf: Math.round(w.backend_saldo_final   ?? 0),
      SI:    Math.round(w.saldoInicial),
      ING:   Math.round(w.ingresos?.total || 0),
      EGR:   Math.round(w.egresos?.total  || 0),
      FN:    Math.round(w.flujoNeto),
      SF:    Math.round(w.saldoFinal),
      neg:   w.saldoFinal < 0,
    }));
    return totals;
  };

  // =====================================================================
  // CÁLCULO DE KPIs "GRADO CFO"
  // Recibe `totals` = displayedTotals (semanas visibles según filtro activo).
  // =====================================================================
  const calculateCFOKPIs = (totals) => {
    if (!totals || totals.length === 0) return null;

    // Clasificar semanas: real/actual = datos históricos confirmados; proyectado = CxC/CxP futuros
    const semanasReales      = totals.filter(w => w.dataType === 'real' || w.dataType === 'actual');
    const semanasProyectadas = totals.filter(w => w.dataType === 'proyectado');

    // ─── 1. NET BURN RATE ────────────────────────────────────────────────────
    // Promedio semanal de caja neta generada (+) o consumida (−) por el negocio.
    // Fórmula: BurnRate = Σ(FlujoNeto_i) / N
    //   FlujoNeto_i = Ingresos_i − Egresos_i + (VentaUSD_i − CompraUSD_i)
    //   N = número de semanas del tipo analizado (reales o proyectadas)
    // Se separa en "real" (pagos confirmados) y "proyectado" (CxC/CxP futuros)
    // para detectar cambios de tendencia entre el pasado y el pronóstico.
    // Interpretación: positivo = saludable; negativo = consumo de reservas (alerta).
    const burnRateReal = semanasReales.length > 0
      ? semanasReales.reduce((sum, w) => sum + w.flujoNeto, 0) / semanasReales.length
      : 0;
    const burnRateProyectado = semanasProyectadas.length > 0
      ? semanasProyectadas.reduce((sum, w) => sum + w.flujoNeto, 0) / semanasProyectadas.length
      : 0;

    // ─── 2. TOTALES REALES Y PROYECTADOS ─────────────────────────────────────
    // Suma directa de ingresos/egresos de las semanas de cada tipo.
    const totalIngresosReales      = semanasReales.reduce((sum, w) => sum + w.ingresos.total, 0);
    const totalEgresosReales       = semanasReales.reduce((sum, w) => sum + w.egresos.total,  0);
    const totalFlujoNetoReal       = semanasReales.reduce((sum, w) => sum + w.flujoNeto, 0);
    const totalIngresosProyectados = semanasProyectadas.reduce((sum, w) => sum + w.ingresos.total, 0);
    const totalEgresosProyectados  = semanasProyectadas.reduce((sum, w) => sum + w.egresos.total,  0);
    const totalFlujoNetoProyectado = semanasProyectadas.reduce((sum, w) => sum + w.flujoNeto, 0);

    // Promedios semanales proyectados (utilizados en comparativas)
    const promedioIngresosSemanal = totalIngresosProyectados / Math.max(semanasProyectadas.length, 1);
    const promedioEgresosSemanal  = totalEgresosProyectados  / Math.max(semanasProyectadas.length, 1);

    // ─── 3. CASH GAP ANALYSIS ────────────────────────────────────────────────
    // Compara el saldo proyectado de cada semana contra el umbral mínimo de operación.
    // Fórmula: Gap_i = SaldoFinal_i − UmbralMínimoOperativo
    //   "Semana en riesgo" = semana donde SaldoFinal_i < UmbralMínimoOperativo
    // UmbralMínimoOperativo: configurable en Ajustes (default: $500,000 MXN).
    //   Representa la caja mínima para cubrir gastos operativos del período.
    // Semana crítica = semana con el saldo final más bajo (mayor vulnerabilidad).
    // Semáforo: 0 en riesgo = cómodo; 1-2 = atención; >2 = intervención urgente.
    const cashGapByWeek = totals.map(w => ({
      semana:    w.displayLabel || w.label,
      saldoFinal: w.saldoFinal,
      gap:        w.saldoFinal - umbralMinimoCaja,
      enRiesgo:   w.saldoFinal < umbralMinimoCaja,
    }));
    const semanasEnRiesgo = cashGapByWeek.filter(w => w.enRiesgo);
    const semanaCritica   = cashGapByWeek.reduce(
      (min, w) => w.saldoFinal < min.saldoFinal ? w : min,
      cashGapByWeek[0] || { saldoFinal: 0, semana: 'N/A' }
    );

    // ─── 4. FLUJO DE CAJA ACUMULADO ──────────────────────────────────────────
    // Acumula el flujo neto semana a semana, separando la curva real de la proyectada.
    // La curva proyectada parte del último valor real acumulado (continuidad).
    let acumuladoReal       = 0;
    let acumuladoProyectado = 0;
    const flujoAcumulado = totals.map(w => {
      if (w.dataType === 'real' || w.dataType === 'actual') {
        acumuladoReal += w.flujoNeto;
        return { ...w, acumuladoReal, acumuladoProyectado };
      } else {
        acumuladoProyectado += w.flujoNeto;
        return { ...w, acumuladoReal, acumuladoProyectado: acumuladoReal + acumuladoProyectado };
      }
    });

    // ─── 5. VOLATILIDAD DEL FLUJO ────────────────────────────────────────────
    // Mide la irregularidad del flujo neto semanal con desviación estándar poblacional.
    // Fórmula σ: √( Σ(FlujoNeto_i − μ)² / N )   μ = BurnRateReal, N = semanas reales
    // Coeficiente de Variación (CV) normaliza σ para comparar períodos distintos:
    //   CV = (σ / |BurnRateReal|) × 100%
    // Umbrales: CV < 25% = flujo estable (bajo riesgo operativo)
    //           CV 25-50% = volatilidad moderada (monitorear de cerca)
    //           CV > 50%  = alta volatilidad (riesgo de déficit inesperado)
    // Solo usa semanas reales para evitar distorsión por datos proyectados.
    let volatilidad = 0;
    if (semanasReales.length > 1) {
      const mediaFlujo    = totalFlujoNetoReal / semanasReales.length;
      const sumaCuadrados = semanasReales.reduce((sum, w) => sum + Math.pow(w.flujoNeto - mediaFlujo, 2), 0);
      volatilidad         = Math.sqrt(sumaCuadrados / semanasReales.length);
    }
    const coeficienteVariacion = burnRateReal !== 0 ? (volatilidad / Math.abs(burnRateReal)) * 100 : 0;

    // ─── 6. RUNWAY ───────────────────────────────────────────────────────────
    // Semanas de operación disponibles con el saldo actual sin ingresos adicionales.
    // Fórmula: Runway = SaldoActual / EgresoPromedioSemanal
    //   SaldoActual = saldoFinal de la semana dataType==='actual' (o primera semana visible)
    //   EgresoPromedio = (ΣEgresosReales + ΣEgresosProyectados) / TotalSemanasVisibles
    // Umbrales: ≥16 semanas = posición cómoda; 8-15 = atención; <8 = alerta crítica.
    const saldoActual    = totals.find(w => w.dataType === 'actual')?.saldoFinal || totals[0]?.saldoFinal || 0;
    const egresoPromedio = (totalEgresosReales + totalEgresosProyectados) / Math.max(totals.length, 1);
    const runway         = egresoPromedio > 0 ? Math.floor(saldoActual / egresoPromedio) : 999;

    // ─── 7. RATIO COBRANZA VS PAGOS ──────────────────────────────────────────
    // Mide la autosuficiencia operativa: ¿cobra la empresa más de lo que paga?
    // Fórmula: Ratio = IngresosReales / EgresosReales (solo semanas reales confirmadas)
    // Ratio > 1.0 = autosuficiente; = 1.0 = equilibrio; < 1.0 = déficit (consume reservas).
    const ratioCobranzaPagos = totalEgresosReales > 0
      ? totalIngresosReales / totalEgresosReales
      : totalIngresosReales > 0 ? 999 : 1;
    
    return {
      // Net Burn Rate
      burnRateReal,
      burnRateProyectado,
      burnRateDelta: burnRateProyectado - burnRateReal,
      
      // Totales
      totalIngresosReales,
      totalEgresosReales,
      totalFlujoNetoReal,
      totalIngresosProyectados,
      totalEgresosProyectados,
      totalFlujoNetoProyectado,
      
      // Promedios
      promedioIngresosSemanal,
      promedioEgresosSemanal,
      
      // Cash Gap
      cashGapByWeek,
      semanasEnRiesgo,
      semanaCritica,
      
      // Flujo Acumulado
      flujoAcumulado,
      acumuladoRealFinal: acumuladoReal,
      acumuladoProyectadoFinal: acumuladoReal + acumuladoProyectado,
      
      // Volatilidad
      volatilidad,
      coeficienteVariacion,
      
      // Operacionales
      runway,
      ratioCobranzaPagos,
      
      // Metadata
      semanasRealesCount: semanasReales.length,
      semanasProyectadasCount: semanasProyectadas.length
    };
  };

  // =====================================================================
  // PREPARAR DATOS PARA GRÁFICOS
  // =====================================================================
  const prepareChartData = (totals) => {
    if (!totals || totals.length === 0) return [];
    
    let acumuladoReal = 0;
    let acumuladoProyectado = 0;
    
    return totals.map((week, idx) => {
      const ingresosMXN = convertToCurrency(week.ingresos.total || 0);
      const egresosMXN = convertToCurrency(week.egresos.total || 0);
      const flujoNeto = convertToCurrency(week.flujoNeto || 0);
      const saldoFinal = convertToCurrency(week.saldoFinal || 0);
      const umbral = convertToCurrency(umbralMinimoCaja);
      
      // Calcular acumulados
      if (week.dataType === 'real' || week.dataType === 'actual') {
        acumuladoReal += flujoNeto;
      } else {
        acumuladoProyectado += flujoNeto;
      }
      
      return {
        semana: week.label,
        fecha: week.dateLabel,
        tipo: week.dataType,
        ingresos: ingresosMXN,
        egresos: egresosMXN,
        flujoNeto: flujoNeto,
        saldoFinal: saldoFinal,
        umbralMinimo: umbral,
        cashGap: saldoFinal - umbral,
        acumuladoReal: week.dataType === 'real' || week.dataType === 'actual' ? acumuladoReal : null,
        acumuladoProyectado: acumuladoReal + acumuladoProyectado,
        // For area chart fill
        flujoNetoPositivo: flujoNeto >= 0 ? flujoNeto : 0,
        flujoNetoNegativo: flujoNeto < 0 ? flujoNeto : 0
      };
    });
  };

  // =====================================================================
  // EXPORTAR A PDF
  // =====================================================================
  const exportToPDF = async () => {
    setExportingPdf(true);
    toast.info('Generando PDF, por favor espere...');

    try {
      const empresa = companyConfig?.nombre || 'TaxnFin';
      const rfc     = companyConfig?.rfc || '';
      const fecha   = new Date().toLocaleDateString('es-MX', {
        day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit',
      });

      const fmt = (n) => {
        if (!n && n !== 0) return '$0K';
        const neg = n < 0;
        const abs = Math.abs(n);
        const s = abs >= 1000 ? `$${(abs / 1000).toFixed(0)}K` : `$${abs.toFixed(0)}`;
        return neg ? `-${s}` : s;
      };

      const pdf  = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a3', compress: true });
      const W    = pdf.internal.pageSize.getWidth();
      const H    = pdf.internal.pageSize.getHeight();
      const margin = 12;

      // ── HEADER navy ──────────────────────────────────────────────────────
      pdf.setFillColor(15, 23, 42);
      pdf.rect(0, 0, W, 30, 'F');
      pdf.setFontSize(7);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(100, 180, 255);
      pdf.text('PROYECCIÓN DE FLUJO DE EFECTIVO · 18 SEMANAS ROLLING', margin, 10);
      pdf.setFontSize(20);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(255, 255, 255);
      pdf.text(empresa, margin, 22);
      if (rfc) {
        pdf.setFontSize(8);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(148, 163, 184);
        pdf.text(`RFC: ${rfc}`, margin, 28);
      }
      pdf.setFontSize(8);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(148, 163, 184);
      pdf.text(fecha, W - margin, 14, { align: 'right' });
      pdf.setTextColor(100, 180, 255);
      pdf.text('Análisis: TaxnFin CFO Intelligence', W - margin, 22, { align: 'right' });

      // ── KPI CARDS ─────────────────────────────────────────────────────────
      const rn  = cfoKPIs?.totalFlujoNetoReal        ?? 0;
      const pn  = cfoKPIs?.acumuladoProyectadoFinal  ?? 0;
      const ir  = cfoKPIs?.totalIngresosReales        ?? 0;
      const er  = cfoKPIs?.totalEgresosReales         ?? 0;
      const ip  = cfoKPIs?.totalIngresosProyectados   ?? 0;
      const ep  = cfoKPIs?.totalEgresosProyectados    ?? 0;
      const br  = cfoKPIs?.burnRateReal               ?? 0;
      const rwy = cfoKPIs?.runway                     ?? 0;
      const wrk = cfoKPIs?.semanasEnRiesgo?.length    ?? 0;

      const kpiCards = [
        { label: 'FLUJO NETO REAL ACUMULADO',   text: fmt(rn),  color: rn >= 0  ? [22,163,74] : [220,38,38] },
        { label: 'FLUJO NETO PROYECTADO TOTAL', text: fmt(pn),  color: pn >= 0  ? [22,163,74] : [220,38,38] },
        { label: 'INGRESOS REALES',             text: fmt(ir),  color: [22,163,74]  },
        { label: 'EGRESOS REALES',              text: fmt(er),  color: [220,38,38]  },
        { label: 'INGRESOS PROYECTADOS',        text: fmt(ip),  color: [22,163,74]  },
        { label: 'EGRESOS PROYECTADOS',         text: fmt(ep),  color: [220,38,38]  },
        { label: 'BURN RATE REAL / SEM',        text: fmt(br),  color: [59,130,246] },
        { label: 'RUNWAY (SEMANAS)',            text: `${rwy} sem`, color: rwy < 8 ? [220,38,38] : [22,163,74] },
        { label: 'SEMANAS EN RIESGO',           text: `${wrk} visible(s)`, color: wrk > 0 ? [220,38,38] : [22,163,74] },
      ];

      const cols  = 3;
      const cardW = (W - margin * 2 - (cols - 1) * 4) / cols;
      const cardH = 20;
      let cardY   = 36;

      kpiCards.forEach((card, i) => {
        if (i > 0 && i % cols === 0) cardY += cardH + 4;
        const cx = margin + (i % cols) * (cardW + 4);

        pdf.setFillColor(248, 250, 252);
        pdf.setDrawColor(226, 232, 240);
        pdf.setLineWidth(0.3);
        pdf.roundedRect(cx, cardY, cardW, cardH, 2, 2, 'FD');

        pdf.setFontSize(6.5);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(100, 116, 139);
        pdf.text(card.label, cx + 4, cardY + 7);

        pdf.setFontSize(13);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(...card.color);
        pdf.text(card.text, cx + 4, cardY + 16);
      });

      // ── LÍNEA + ANÁLISIS NARRATIVO ────────────────────────────────────────
      const sepY = cardY + cardH + 8;
      pdf.setDrawColor(226, 232, 240);
      pdf.setLineWidth(0.5);
      pdf.line(margin, sepY, W - margin, sepY);

      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(15, 23, 42);
      pdf.text('Análisis del Período', margin, sepY + 7);

      const runwayTexto = rwy >= 52 ? 'más de un año' : `${rwy} semanas (${(rwy / 4.33).toFixed(1)} meses)`;
      const parrafos = [
        `La empresa registra un flujo neto real acumulado de ${fmt(rn)} en el período analizado. ` +
        `El runway operativo estimado es de ${runwayTexto} considerando el ritmo actual de egresos.`,
        wrk === 0
          ? 'No se identifican semanas críticas de liquidez en el horizonte de 18 semanas proyectadas.'
          : `Se identifican ${wrk} semana(s) con riesgo de liquidez dentro del horizonte proyectado. Se recomienda atención inmediata.`,
        'La tabla en la siguiente página muestra el detalle semanal de ingresos, egresos, flujo neto y saldo final.',
      ];

      let textY = sepY + 14;
      parrafos.forEach(p => {
        const lines = pdf.splitTextToSize(p, W - margin * 2);
        pdf.setFontSize(8.5);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(51, 65, 85);
        pdf.text(lines, margin, textY);
        textY += lines.length * 5 + 4;
      });

      // Footer pág 1
      pdf.setFontSize(7);
      pdf.setTextColor(150, 150, 150);
      pdf.setFont('helvetica', 'normal');
      pdf.text('TaxnFin', margin, H - 4);
      pdf.text(`${empresa} · Proyección de Flujo de Efectivo · 18 Semanas Rolling | ${fecha}`, W / 2, H - 4, { align: 'center' });

      // ── PÁGINAS 2+: TABLA DEL MODELO ─────────────────────────────────────
      await new Promise(r => setTimeout(r, 500));
      const tableEl = document.getElementById('cashflow-table-model');
      if (tableEl) {
        // Medir sidebar ANTES del capture para compensar su ancho en el crop
        const sidebarW = document.querySelector('aside[data-testid="sidebar"]')?.offsetWidth ?? 0;
        const captureW = Math.max(tableEl.scrollWidth, 2800);
        const canvasTable = await html2canvas(tableEl, {
          scale: 0.65,
          useCORS: true,
          allowTaint: true,
          backgroundColor: '#ffffff',
          width:        captureW,
          height:       tableEl.scrollHeight,
          windowWidth:  captureW + sidebarW,
          windowHeight: tableEl.scrollHeight,
          scrollX: -sidebarW,
          onclone: (doc) => {
            doc.querySelectorAll('iframe').forEach(e => e.remove());
            // Eliminar sidebar del clone para que no aparezca negro en el PDF
            doc.querySelector('aside[data-testid="sidebar"]')?.remove();
            const el = doc.getElementById('cashflow-table-model');
            if (el) { el.style.overflow = 'visible'; el.style.width = 'max-content'; el.style.maxWidth = 'none'; }
          },
        });

        const tableImgW = W - margin * 2;
        const contentH  = H - margin * 2 - 8;
        const totalTableH = (canvasTable.height * tableImgW) / canvasTable.width;
        let yPos = 0;

        while (yPos < totalTableH) {
          pdf.addPage();
          const srcY = (yPos / totalTableH) * canvasTable.height;
          const srcH = Math.min((contentH / totalTableH) * canvasTable.height, canvasTable.height - srcY);

          const pc      = document.createElement('canvas');
          pc.width      = canvasTable.width;
          pc.height     = Math.max(srcH, 1);
          const ctx     = pc.getContext('2d');
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, pc.width, pc.height);
          ctx.drawImage(canvasTable, 0, srcY, canvasTable.width, srcH, 0, 0, canvasTable.width, srcH);

          const ph = (srcH * tableImgW) / canvasTable.width;
          pdf.addImage(pc.toDataURL('image/jpeg', 0.92), 'JPEG', margin, margin, tableImgW, ph);

          pdf.setFontSize(7);
          pdf.setTextColor(150, 150, 150);
          pdf.text('TaxnFin', margin, H - 4);
          pdf.text(`${empresa} · Proyección de Flujo de Efectivo · 18 Semanas Rolling | ${fecha}`, W / 2, H - 4, { align: 'center' });
          yPos += contentH;
        }
      }

      // Numeración final
      const totalPages = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);
        pdf.setFontSize(7);
        pdf.setTextColor(150, 150, 150);
        pdf.text(`${i} / ${totalPages}`, W - margin, H - 4, { align: 'right' });
      }

      pdf.save(`TaxnFin_FlujoCaja_${empresa.replace(/\s/g, '_')}_${new Date().toISOString().slice(0, 10)}.pdf`);
      toast.success('PDF generado correctamente');
    } catch (error) {
      const tEl = document.getElementById('cashflow-table-model');
      if (tEl) { tEl.style.overflow = ''; tEl.style.width = ''; }
      console.error('PDF error:', error);
      toast.error('Error: ' + (error?.message || 'desconocido'));
    } finally {
      setExportingPdf(false);
    }
  };

  // =====================================================================
  // DRILL-DOWN: Abrir detalle de una celda
  // =====================================================================
  const handleCellClick = (weekIdx, tipo, categoryName, subcategoryName = null) => {
    const week = weeklyData[weekIdx];
    if (!week) return;
    
    const section = tipo === 'ingreso' ? week.ingresos : week.egresos;
    const category = section.byCategory[categoryName];
    // Nombre legible para el título del modal
    const categoryDisplayName = catDisplayName(categoryName);
    
    if (!category) return;
    
    let items = [];
    let total = 0;
    
    if (subcategoryName) {
      // Drill-down to subcategory level
      const subcategory = category.bySubcategory?.[subcategoryName];
      if (subcategory) {
        items = subcategory.items || [];
        total = subcategory.total || 0;
      }
    } else {
      // Drill-down to category level
      items = category.items || [];
      total = category.total || 0;
    }
    
    // Enrich items with full details from payments, CFDIs, and reconciliations
    const enrichedItems = items.map(item => {
      const payment = allPayments.find(p => p.id === item.id);
      // Buscar CFDI: por uuid, por cfdiId explícito, o por id del ítem (para ítems de processed_cfdis)
      const cfdi = cfdis.find(c => 
        (item.uuid && c.uuid === item.uuid) || 
        (item.cfdiId && c.id === item.cfdiId) || 
        (!item.es_proyeccion && item.id && c.id === item.id && !item.es_real)
      );
      const bankTxn = payment?.bank_transaction_id 
        ? bankTransactions.find(t => t.id === payment.bank_transaction_id)
        : null;
      const reconciliation = bankTxn 
        ? reconciliations.find(r => r.bank_transaction_id === bankTxn.id)
        : null;
      
      // Get bank account info
      const bankAccountId = item.bankAccountId || payment?.bank_account_id;
      const bankAccount = bankAccountId ? bankAccounts.find(b => b.id === bankAccountId) : null;
      
      // Get vendor/customer info - prioritize item's own data first
      let tercero = '';
      let terceroTipo = '';
      
      // First try: Use item's direct emisor/receptor data (from CFDI processing)
      if (tipo === 'ingreso') {
        tercero = item.nombre || item.concepto || item.receptor || item.beneficiario || '';
        terceroTipo = 'cliente';
      } else {
        tercero = item.nombre || item.concepto || item.emisor || item.beneficiario || '';
        terceroTipo = 'proveedor';
      }
      
      // Second try: Get from payment's vendor/customer
      if (!tercero && payment?.vendor_id) {
        const vendor = vendors.find(v => v.id === payment.vendor_id);
        tercero = vendor?.nombre || payment.beneficiario || '';
        terceroTipo = 'proveedor';
      } else if (!tercero && payment?.customer_id) {
        const customer = customers.find(c => c.id === payment.customer_id);
        tercero = customer?.nombre || payment.beneficiario || '';
        terceroTipo = 'cliente';
      }
      
      // Third try: Get from CFDI emisor/receptor
      if (!tercero && cfdi) {
        if (tipo === 'ingreso') {
          const customer = customers.find(c => c.rfc === cfdi.receptor_rfc);
          tercero = customer?.nombre || cfdi.receptor_nombre || '';
          terceroTipo = 'cliente';
        } else {
          const vendor = vendors.find(v => v.rfc === cfdi.emisor_rfc);
          tercero = vendor?.nombre || cfdi.emisor_nombre || '';
          terceroTipo = 'proveedor';
        }
      }
      
      // Fourth try: Use beneficiario from payment
      if (!tercero && payment?.beneficiario) {
        tercero = payment.beneficiario;
        terceroTipo = tipo === 'ingreso' ? 'cliente' : 'proveedor';
      }
      
      // Fifth try: For bank fees, use bank name
      if (!tercero && bankAccount) {
        tercero = bankAccount.banco || bankAccount.nombre || 'Banco';
        terceroTipo = 'proveedor'; // Bank fees are always providers
      }
      
      // Sixth try: Extract from concepto if it's a bank fee
      if (!tercero && item.concepto) {
        const concepto = item.concepto.toLowerCase();
        if (concepto.includes('comisión') || concepto.includes('comision') || 
            concepto.includes('iva') || concepto.includes('cargo')) {
          // Use bank name from bank account or default
          tercero = bankAccount?.banco || 'Cargo Bancario';
          terceroTipo = 'proveedor';
        }
      }
      
      return {
        ...item,
        paymentId: payment?.id,
        concepto: item.concepto || payment?.concepto || cfdi?.concepto || '',
        tercero: tercero || 'Sin asignar',
        terceroTipo: terceroTipo || (tipo === 'ingreso' ? 'cliente' : 'proveedor'),
        uuid: item.uuid || cfdi?.uuid || '',
        folio: cfdi?.folio || '',
        fechaFactura: cfdi?.fecha_emision || payment?.fecha_pago || item.fecha,
        montoOriginal: payment?.monto || cfdi?.total || item.monto,
        moneda: payment?.moneda || cfdi?.moneda || item.moneda || 'MXN',
        // Bank transaction info
        bankTxnId: bankTxn?.id,
        bankTxnDescripcion: bankTxn?.descripcion,
        bankTxnFecha: bankTxn?.fecha,
        bankTxnMonto: bankTxn?.monto,
        // Bank account info
        bankAccountName: bankAccount?.banco || bankAccount?.nombre || '',
        // Reconciliation status
        conciliado: !!reconciliation,
        reconciliacionId: reconciliation?.id,
        tipoConciliacion: reconciliation?.tipo_conciliacion,
        // Source
        source: item.source
      };
    });
    
    setDrillDownData({
      weekNum: weekIdx + 1,
      weekLabel: week.displayLabel || week.label || `S${weekIdx + 1}`,
      dateLabel: week.dateLabel || '',
      dataType: week.dataType,
      tipo,
      categoryName: categoryDisplayName,
      subcategoryName,
      items: enrichedItems,
      total
    });
    setDrillDownOpen(true);
  };

  // =====================================================================
  // VISTA POR PROVEEDOR/CLIENTE: Procesar datos agrupados por tercero
  // =====================================================================
  const processDataByParty = (sourceWeeks = weeklyData) => {
    // Create a map: { terceroId: { nombre, tipo, weeks: { weekIdx: { ingresos, egresos } } } }
    const partyMap = {};

    sourceWeeks.forEach((week, weekIdx) => {
      // Process ingresos
      Object.entries(week.ingresos.byCategory).forEach(([catName, catData]) => {
        (catData.items || []).forEach(item => {
          const payment = allPayments.find(p => p.id === item.id);
          let terceroId = 'sin-asignar';
          let terceroNombre = 'Sin Asignar';
          let terceroTipo = 'cliente';
          
          if (payment?.customer_id) {
            const customer = customers.find(c => c.id === payment.customer_id);
            if (customer) {
              terceroId = customer.id;
              terceroNombre = customer.nombre;
              terceroTipo = 'cliente';
            }
          }
          // Try to find customer by RFC from CFDI
          if (terceroId === 'sin-asignar') {
            const cfdi = cfdis.find(c => c.uuid === item.uuid);
            if (cfdi?.receptor_rfc) {
              const customer = customers.find(c => c.rfc === cfdi.receptor_rfc);
              if (customer) {
                terceroId = customer.id;
                terceroNombre = customer.nombre;
                terceroTipo = 'cliente';
              } else if (cfdi?.receptor_nombre) {
                // Use CFDI receptor name directly (Contalink sync data)
                terceroId = cfdi.receptor_rfc;
                terceroNombre = cfdi.receptor_nombre;
                terceroTipo = 'cliente';
              }
            } else if (cfdi?.receptor_nombre) {
              terceroId = cfdi.receptor_nombre;
              terceroNombre = cfdi.receptor_nombre;
              terceroTipo = 'cliente';
            }
          }
          // Last fallback: use payment beneficiario (set by Contalink sync)
          if (terceroId === 'sin-asignar' && payment?.beneficiario) {
            terceroId = payment.beneficiario;
            terceroNombre = payment.beneficiario;
            terceroTipo = 'cliente';
          }
          // Fallback para ítems sintéticos (CxC/CxP proyectados, custom concepts):
          // item.beneficiario ya trae el nombre real del cliente/proveedor
          if (terceroId === 'sin-asignar' && item.beneficiario) {
            terceroId = item.beneficiario;
            terceroNombre = item.beneficiario;
            terceroTipo = 'cliente';
          }

          if (!partyMap[terceroId]) {
            partyMap[terceroId] = {
              id: terceroId,
              nombre: terceroNombre,
              tipo: terceroTipo,
              weeks: {}
            };
          }

          if (!partyMap[terceroId].weeks[weekIdx]) {
            partyMap[terceroId].weeks[weekIdx] = { ingresos: 0, egresos: 0, items: [] };
          }

          partyMap[terceroId].weeks[weekIdx].ingresos += item.monto || 0;
          partyMap[terceroId].weeks[weekIdx].items.push({ ...item, tipo: 'ingreso' });
        });
      });

      // Process egresos
      Object.entries(week.egresos.byCategory).forEach(([catName, catData]) => {
        (catData.items || []).forEach(item => {
          const payment = allPayments.find(p => p.id === item.id);
          let terceroId = 'sin-asignar';
          let terceroNombre = 'Sin Asignar';
          let terceroTipo = 'proveedor';
          
          if (payment?.vendor_id) {
            const vendor = vendors.find(v => v.id === payment.vendor_id);
            if (vendor) {
              terceroId = vendor.id;
              terceroNombre = vendor.nombre;
              terceroTipo = 'proveedor';
            }
          }
          // Try to find vendor by RFC from CFDI
          if (terceroId === 'sin-asignar') {
            const cfdi = cfdis.find(c => c.uuid === item.uuid);
            if (cfdi?.emisor_rfc) {
              const vendor = vendors.find(v => v.rfc === cfdi.emisor_rfc);
              if (vendor) {
                terceroId = vendor.id;
                terceroNombre = vendor.nombre;
                terceroTipo = 'proveedor';
              } else if (cfdi?.emisor_nombre) {
                // Use CFDI emisor name directly (Contalink sync data)
                terceroId = cfdi.emisor_rfc;
                terceroNombre = cfdi.emisor_nombre;
                terceroTipo = 'proveedor';
              }
            } else if (cfdi?.emisor_nombre) {
              terceroId = cfdi.emisor_nombre;
              terceroNombre = cfdi.emisor_nombre;
              terceroTipo = 'proveedor';
            }
          }
          // Last fallback: use payment beneficiario (set by Contalink sync)
          if (terceroId === 'sin-asignar' && payment?.beneficiario) {
            terceroId = payment.beneficiario;
            terceroNombre = payment.beneficiario;
            terceroTipo = 'proveedor';
          }
          // Fallback para ítems sintéticos (CxC/CxP proyectados, custom concepts):
          // item.beneficiario ya trae el nombre real del cliente/proveedor
          if (terceroId === 'sin-asignar' && item.beneficiario) {
            terceroId = item.beneficiario;
            terceroNombre = item.beneficiario;
            terceroTipo = 'proveedor';
          }

          if (!partyMap[terceroId]) {
            partyMap[terceroId] = {
              id: terceroId,
              nombre: terceroNombre,
              tipo: terceroTipo,
              weeks: {}
            };
          }
          
          if (!partyMap[terceroId].weeks[weekIdx]) {
            partyMap[terceroId].weeks[weekIdx] = { ingresos: 0, egresos: 0, items: [] };
          }
          
          partyMap[terceroId].weeks[weekIdx].egresos += item.monto || 0;
          partyMap[terceroId].weeks[weekIdx].items.push({ ...item, tipo: 'egreso' });
        });
      });
    });
    
    return Object.values(partyMap);
  };

  // Filter party data based on current filters
  const filterPartyData = (partyData) => {
    return partyData.filter(party => {
      // Filter by search term
      if (partyFilters.searchTerm.trim() !== '') {
        const searchLower = partyFilters.searchTerm.toLowerCase();
        if (!party.nombre.toLowerCase().includes(searchLower)) {
          return false;
        }
      }
      
      // Filter by tipo tercero
      if (partyFilters.tipoTercero !== 'todos') {
        if (party.tipo !== partyFilters.tipoTercero) {
          return false;
        }
      }
      
      // Filter by saldo tipo
      if (partyFilters.saldoTipo !== 'todos') {
        const totalIngresos = Object.values(party.weeks).reduce((s, w) => s + w.ingresos, 0);
        const totalEgresos = Object.values(party.weeks).reduce((s, w) => s + w.egresos, 0);
        const netTotal = totalIngresos - totalEgresos;
        
        if (partyFilters.saldoTipo === 'positivo' && netTotal <= 0) {
          return false;
        }
        if (partyFilters.saldoTipo === 'negativo' && netTotal >= 0) {
          return false;
        }
      }
      
      return true;
    });
  };

  // Check if party filters are active
  const hasPartyFiltersActive = () => {
    return partyFilters.searchTerm.trim() !== '' || 
           partyFilters.tipoTercero !== 'todos' || 
           partyFilters.saldoTipo !== 'todos';
  };

  // Reset party filters
  const resetPartyFilters = () => {
    setPartyFilters({
      searchTerm: '',
      tipoTercero: 'todos',
      saldoTipo: 'todos'
    });
  };

  // Export party data (filtered or all)
  const exportPartyReport = () => {
    const partyData = processDataByParty(calculateRunningTotals());
    const filteredParties = filterPartyData(partyData);
    
    if (filteredParties.length === 0) {
      toast.error('No hay datos para exportar');
      return;
    }
    
    const reportData = [];
    
    filteredParties.forEach(party => {
      // Calculate totals for this party
      const totalIngresos = Object.values(party.weeks).reduce((s, w) => s + w.ingresos, 0);
      const totalEgresos = Object.values(party.weeks).reduce((s, w) => s + w.egresos, 0);
      const netTotal = totalIngresos - totalEgresos;
      
      // Create a row for each week with data
      weeklyData.forEach((week, weekIdx) => {
        const weekData = party.weeks[weekIdx];
        if (!weekData || (weekData.ingresos === 0 && weekData.egresos === 0)) return;
        
        const weekLabel = week.displayLabel || `S${weekIdx + 1}`;
        const dataType = week.dataType === 'real' ? 'Real' : week.dataType === 'actual' ? 'Actual' : 'Proyectado';

        reportData.push({
          'Proveedor/Cliente': party.nombre,
          'Tipo Tercero': party.tipo === 'cliente' ? 'Cliente' : 'Proveedor',
          'Semana': weekLabel,
          'Fecha': week.dateLabel,
          'Tipo Dato': dataType,
          'Ingresos': weekData.ingresos,
          'Egresos': weekData.egresos,
          'Neto Semana': weekData.ingresos - weekData.egresos,
          'Total Ingresos': totalIngresos,
          'Total Egresos': totalEgresos,
          'Saldo Neto Total': netTotal
        });
      });
    });
    
    if (reportData.length === 0) {
      toast.error('No hay movimientos para exportar');
      return;
    }
    
    const isFiltered = hasPartyFiltersActive();
    const filename = isFiltered ? 'Terceros_Filtrado' : 'Terceros_18_Semanas';
    
    const success = exportToExcel(reportData, filename, 'Por Tercero');
    if (success) {
      toast.success(isFiltered ? 'Terceros filtrados exportados a Excel' : 'Datos de terceros exportados a Excel');
    } else {
      toast.error('Error al exportar');
    }
  };

  // =====================================================================
  // EXPORTAR REPORTE DETALLADO
  // =====================================================================
  const exportDetailReport = () => {
    const reportData = [];
    
    weeklyData.forEach((week, weekIdx) => {
      const weekLabel = week.displayLabel || `S${weekIdx + 1}`;
      const dataType = week.dataType === 'real' ? 'Real' : week.dataType === 'actual' ? 'Actual' : 'Proyectado';

      // Process ingresos
      Object.entries(week.ingresos.byCategory).forEach(([catName, catData]) => {
        (catData.items || []).forEach(item => {
          const payment = allPayments.find(p => p.id === item.id);
          const cfdi = cfdis.find(c => c.uuid === item.uuid);
          const bankTxn = payment?.bank_transaction_id ? bankTransactions.find(t => t.id === payment.bank_transaction_id) : null;
          
          let tercero = item.beneficiario || payment?.beneficiario || '';
          if (payment?.customer_id) {
            const customer = customers.find(c => c.id === payment.customer_id);
            tercero = customer?.nombre || tercero;
          }
          
          reportData.push({
            'Semana': weekLabel,
            'Fecha': week.dateLabel,
            'Tipo Dato': dataType,
            'Categoría': catName,
            'Tipo': 'Ingreso',
            'Cliente/Proveedor': tercero,
            'UUID': item.uuid || cfdi?.uuid || '',
            'Folio': cfdi?.folio || '',
            'Fecha Factura': cfdi?.fecha_emision ? format(new Date(cfdi.fecha_emision), 'dd/MM/yyyy') : '',
            'Monto': item.monto,
            'Moneda': payment?.moneda || cfdi?.moneda || 'MXN',
            'Monto MXN': item.monto,
            'Cuenta Bancaria': bankTxn?.banco || '',
            'Conciliado': bankTxn ? 'Sí' : 'No',
            'Movimiento Bancario': bankTxn?.descripcion || ''
          });
        });
      });
      
      // Process egresos
      Object.entries(week.egresos.byCategory).forEach(([catName, catData]) => {
        (catData.items || []).forEach(item => {
          const payment = allPayments.find(p => p.id === item.id);
          const cfdi = cfdis.find(c => c.uuid === item.uuid);
          const bankTxn = payment?.bank_transaction_id ? bankTransactions.find(t => t.id === payment.bank_transaction_id) : null;
          
          let tercero = item.beneficiario || payment?.beneficiario || '';
          if (payment?.vendor_id) {
            const vendor = vendors.find(v => v.id === payment.vendor_id);
            tercero = vendor?.nombre || tercero;
          }
          
          reportData.push({
            'Semana': weekLabel,
            'Fecha': week.dateLabel,
            'Tipo Dato': dataType,
            'Categoría': catName,
            'Tipo': 'Egreso',
            'Cliente/Proveedor': tercero,
            'UUID': item.uuid || cfdi?.uuid || '',
            'Folio': cfdi?.folio || '',
            'Fecha Factura': cfdi?.fecha_emision ? format(new Date(cfdi.fecha_emision), 'dd/MM/yyyy') : '',
            'Monto': item.monto,
            'Moneda': payment?.moneda || cfdi?.moneda || 'MXN',
            'Monto MXN': item.monto,
            'Cuenta Bancaria': bankTxn?.banco || '',
            'Conciliado': bankTxn ? 'Sí' : 'No',
            'Movimiento Bancario': bankTxn?.descripcion || ''
          });
        });
      });
    });
    
    if (reportData.length === 0) {
      toast.error('No hay datos para exportar');
      return;
    }
    
    const success = exportToExcel(reportData, 'Detalle_Cashflow', 'Detalle');
    if (success) {
      toast.success('Reporte de detalle exportado a Excel');
    } else {
      toast.error('Error al exportar');
    }
  };

  // ── Mapa de renombrado de categorías genéricas → legibles ───────────────
  const CAT_DISPLAY_NAME = {
    'cobro_alegra':  'Cobros',
    'banco_alegra':  'Pagos',
    'pago_alegra':   'Pagos',
    'proyeccion':    'Proyección',
  };
  const catDisplayName = (cat) => CAT_DISPLAY_NAME[cat] || cat;

  // Única fuente de verdad para la vista mensual: agrega weeklyTotals por mes.
  // Garantiza que mensual === semanal para los mismos períodos.
  // saldoInicial del mes = saldoInicial de la primera semana del mes
  // saldoFinal  del mes = saldoFinal  de la última semana del mes
  const buildMonthlyFromWeeks = (weekTotals) => {
    const monthMap = {};
    const todayRef = new Date();
    weekTotals.forEach(week => {
      const key = format(week.weekStart, 'yyyy-MM');
      if (!monthMap[key]) {
        const mStart = startOfMonth(week.weekStart);
        const mEnd   = addMonths(mStart, 1);
        monthMap[key] = {
          label:              format(mStart, 'MMM yyyy', { locale: es }),
          monthStart:         mStart,
          monthEnd:           mEnd,
          isPast:             mEnd <= todayRef,
          isCurrent:          mStart <= todayRef && todayRef < mEnd,
          ingresos:           { total: 0, byCategory: {} },
          egresos:            { total: 0, byCategory: {} },
          saldoInicial:       null,
          saldoFinal:         null,
          flujoNeto:          0,
          traspasos_ingreso:  0,
          traspasos_egreso:   0,
          sinClasificar:      [],   // items sin_clasificar de todas las semanas del mes
        };
      }
      const m = monthMap[key];
      if (m.saldoInicial === null) m.saldoInicial = week.saldoInicial ?? 0;
      m.saldoFinal = week.saldoFinal ?? 0;
      m.flujoNeto  = (m.saldoFinal - m.saldoInicial);

      m.traspasos_ingreso += week.traspasos_ingreso || 0;
      m.traspasos_egreso  += week.traspasos_egreso  || 0;

      m.ingresos.total += week.ingresos.total || 0;
      m.egresos.total  += week.egresos.total  || 0;

      // Acumular byCategory con nombre legible
      Object.entries(week.ingresos.byCategory || {}).forEach(([cat, catData]) => {
        const nombre = catDisplayName(cat);
        m.ingresos.byCategory[nombre] = (m.ingresos.byCategory[nombre] || 0) + (catData.total || 0);
      });
      Object.entries(week.egresos.byCategory || {}).forEach(([cat, catData]) => {
        const nombre = catDisplayName(cat);
        m.egresos.byCategory[nombre] = (m.egresos.byCategory[nombre] || 0) + (catData.total || 0);
      });

      // Recolectar items sin clasificar del detalle backend
      const sinIng = (week.backend_ingresos_detalle || []).filter(i => i.sin_clasificar);
      const sinEgr = (week.backend_egresos_detalle || []).filter(i => i.sin_clasificar);
      m.sinClasificar.push(...sinIng, ...sinEgr);
    });
    return Object.values(monthMap).sort((a, b) => a.monthStart - b.monthStart);
  };

  // useCallback sin deps: función estable que siempre lee los valores más recientes via kpiStateRef
  const openKpiModal = useCallback(async (kpiKey) => {
    console.log('KPI CLICK', kpiKey);
    const { cfoKPIs: kpis, KPI_DEFS: defs, formatCurrency: fmt } = kpiStateRef.current;
    if (!kpis || !defs) {
      console.warn('[KPI Modal] kpis o defs no disponibles aún', { kpis: !!kpis, defs: !!defs });
      return;
    }
    try {
      const def = defs[kpiKey];
      const values = def.getValues(kpis, fmt);

      let status = 'neutral';
      if (kpiKey === 'burnRate') {
        status = kpis.burnRateReal >= 0 ? 'good'
               : kpis.burnRateReal >= -(kpis.promedioEgresosSemanal * 0.1) ? 'warning' : 'danger';
      } else if (kpiKey === 'cashGap') {
        status = kpis.semanasEnRiesgo.length === 0 ? 'good'
               : kpis.semanasEnRiesgo.length <= 2 ? 'warning' : 'danger';
      } else if (kpiKey === 'volatilidad') {
        status = kpis.coeficienteVariacion < 25 ? 'good'
               : kpis.coeficienteVariacion < 50 ? 'warning' : 'danger';
      } else if (kpiKey === 'operativos') {
        status = (kpis.runway >= 16 && kpis.ratioCobranzaPagos >= 1) ? 'good'
               : (kpis.runway >= 8 || kpis.ratioCobranzaPagos >= 0.8) ? 'warning' : 'danger';
      }

      setKpiModal({ open: true, name: def.name, formula: def.formula, description: def.description, values, insight: '', loading: true, status, kpiKey });

      try {
        const res = await api.post('/cashflow-kpi/insight', {
          kpi_name: def.name,
          formula: def.formula,
          description: def.description,
          values: Object.fromEntries(Object.entries(values).map(([k, v]) => [k, String(v)])),
        });
        setKpiModal(prev => ({ ...prev, insight: res.data.insight, loading: false }));
      } catch {
        setKpiModal(prev => ({ ...prev, insight: 'No se pudo obtener el análisis en este momento.', loading: false }));
      }
    } catch (err) {
      console.error('[KPI Modal] Error al abrir modal:', err);
      toast.error('Error al abrir el análisis del KPI');
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return <div className="p-8">{t.loading}</div>;

  const weeklyTotals = calculateRunningTotals();

  // Column visibility mask — true = show this week column in the table
  // Default: hide past weeks; show current + future
  const columnVisible = weeklyTotals.map(w => {
    // Fechas robustas: si weekEnd falta o no es Date, derivarla de weekStart+7d
    const ws = w.weekStart instanceof Date ? w.weekStart : new Date(w.weekStart);
    const we = w.weekEnd instanceof Date ? w.weekEnd : new Date(ws.getTime() + 7 * 86400000);
    if (filterFechaInicio) {
      const start = new Date(filterFechaInicio + 'T00:00:00');
      if (we <= start) return false;
    } else {
      if (we <= new Date()) return false; // default: past weeks hidden
    }
    if (filterFechaFin) {
      const end = new Date(filterFechaFin + 'T23:59:59');
      if (ws > end) return false;
    }
    return true;
  });

  // Subset of weeklyTotals for TOTAL column calculations and final-saldo references
  const displayedTotals = weeklyTotals.filter((_, i) => columnVisible[i]);

  // Vista Mensual derivada SOLO de las semanas visibles (respeta el filtro de
  // rango Desde/Hasta, igual que la Vista Semanal) — misma fuente de verdad
  const monthlyData = buildMonthlyFromWeeks(displayedTotals);

  // KPIs y gráficos usan solo las semanas visibles (respetan el filtro de rango)
  const cfoKPIs = calculateCFOKPIs(displayedTotals);

  // ── RUNWAY (conservador y proyectado) — derivado de weeklyTotals ──────────
  // Usa la serie COMPLETA (no displayedTotals): el runway es una métrica absoluta
  // y el filtro default oculta las semanas pasadas, que son la base del cálculo.
  // dataType en este modelo: 'real' | 'actual' (semana en curso) | 'proyectado'.
  const runwayKPI = (() => {
    const reales = weeklyTotals.filter(w => w.dataType === 'real' || w.dataType === 'actual');

    // Paso 1: saldo base = saldo final de la última semana con datos reales
    const saldoBase = reales.length > 0
      ? reales[reales.length - 1].saldoFinal
      : (weeklyTotals[0]?.saldoInicial || 0);

    // Paso 2: net burn histórico (últimas 4 semanas reales)
    // flujoNeto = ingresos − egresos (+ flujo de divisas), el neto semanal del modelo
    const ultimas4 = reales.slice(-4);
    const avgNetBurn = ultimas4.length > 0
      ? ultimas4.reduce((s, w) => s + w.flujoNeto, 0) / ultimas4.length
      : 0;
    // Solo hay runway conservador si en promedio se consume caja (avgNetBurn < 0)
    const conservador = avgNetBurn < 0 ? saldoBase / Math.abs(avgNetBurn) : null;

    // Paso 3: runway proyectado — iterar las semanas proyectadas acumulando flujo
    const proyectadas = weeklyTotals.filter(w => w.dataType === 'proyectado');
    let saldoAcumulado = saldoBase;
    let proyectado = null;
    for (let i = 0; i < proyectadas.length; i++) {
      saldoAcumulado += proyectadas[i].flujoNeto;
      if (saldoAcumulado <= 0) { proyectado = i + 1; break; }
    }
    // proyectado === null → la caja no se agota dentro del horizonte proyectado

    return { saldoBase, avgNetBurn, conservador, proyectado, horizonte: proyectadas.length };
  })();
  const chartData = prepareChartData(displayedTotals);

  const customConceptsIngresos = customConcepts.filter(c => c.tipo === 'ingreso');
  const customConceptsEgresos = customConcepts.filter(c => c.tipo === 'egreso');
  const grandTotalIngresos = displayedTotals.reduce((sum, w) => sum + w.ingresos.total, 0);
  const grandTotalEgresos = displayedTotals.reduce((sum, w) => sum + w.egresos.total, 0);
  const grandTotalCompraUSD = displayedTotals.reduce((sum, w) => sum + (w.compraUSD || 0), 0);
  const grandTotalVentaUSD = displayedTotals.reduce((sum, w) => sum + (w.ventaUSD || 0), 0);
  const grandTotalFlujoDivisas = grandTotalVentaUSD - grandTotalCompraUSD;
  const grandTotalFlujo = grandTotalIngresos - grandTotalEgresos + grandTotalFlujoDivisas;

  // ─── KPI INSIGHT: abre modal y llama a Claude para interpretar el KPI ──────
  const KPI_DEFS = {
    burnRate: {
      name: 'Net Burn Rate',
      formula: 'BurnRate = Σ(FlujoNeto_i) / N   donde FlujoNeto = Ingresos − Egresos + Divisas',
      description: 'Promedio semanal de caja neta generada (>0) o consumida (<0) por el negocio.',
      getValues: (k) => ({
        'Burn Rate Real (promedio semanal)': k.burnRateReal,
        'Burn Rate Proyectado (promedio semanal)': k.burnRateProyectado,
        'Delta Proyectado vs Real': k.burnRateDelta,
        'Semanas reales analizadas': k.semanasRealesCount,
        'Semanas proyectadas': k.semanasProyectadasCount,
      }),
    },
    cashGap: {
      name: 'Cash Gap Analysis',
      formula: 'Gap_i = SaldoFinal_i − UmbralMínimoOperativo',
      description: 'Identifica semanas donde el saldo proyectado cae por debajo del mínimo operativo configurado.',
      getValues: (k, fmt) => ({
        'Umbral mínimo de caja': fmt(umbralMinimoCaja),
        'Semanas en riesgo (en rango visible)': k.semanasEnRiesgo.length,
        'Semana con saldo más bajo': k.semanaCritica?.semana || 'N/A',
        'Saldo en semana crítica': fmt(k.semanaCritica?.saldoFinal || 0),
      }),
    },
    volatilidad: {
      name: 'Volatilidad del Flujo',
      formula: 'σ = √(Σ(FlujoNeto_i − Media)² / N)   |   CV = σ / |BurnRateReal| × 100',
      description: 'Desviación estándar del flujo neto semanal real. CV>50% = alta volatilidad.',
      getValues: (k, fmt) => ({
        'Desviación estándar (σ)': fmt(k.volatilidad),
        'Coeficiente de variación (CV)': `${k.coeficienteVariacion.toFixed(1)}%`,
        'Burn Rate Real (media)': fmt(k.burnRateReal),
        'Nivel de riesgo': k.coeficienteVariacion > 50 ? 'Alto' : k.coeficienteVariacion > 25 ? 'Moderado' : 'Bajo',
      }),
    },
    operativos: {
      name: 'Indicadores Operativos',
      formula: 'Runway = SaldoActual / EgresoPromedioSemanal   |   Ratio = IngresoReal / EgresoReal',
      description: 'Runway: semanas de operación disponibles. Ratio cobranza/pagos: eficiencia de cobro vs pago.',
      getValues: (k, fmt) => ({
        'Runway (semanas de operación)': k.runway === 999 ? 'Sin límite' : `${k.runway} semanas`,
        'Ratio Cobranza / Pagos': k.ratioCobranzaPagos?.toFixed(2),
        'Total Ingresos Reales': fmt(k.totalIngresosReales),
        'Total Egresos Reales': fmt(k.totalEgresosReales),
        'Flujo Neto Real Total': fmt(k.totalFlujoNetoReal),
      }),
    },
  };

  // Sincronizar ref para que openKpiModal (useCallback estable) lea los valores del render actual
  kpiStateRef.current = { cfoKPIs, KPI_DEFS, formatCurrency };

  // Days of week translated
  const DIAS_SEMANA_TR = [
    { value: 0, label: t.sunday },
    { value: 1, label: t.monday },
    { value: 2, label: t.tuesday },
    { value: 3, label: t.wednesday },
    { value: 4, label: t.thursday },
    { value: 5, label: t.friday },
    { value: 6, label: t.saturday }
  ];

  return (
    <div className="p-6 space-y-6 bg-[#F8FAFC] min-h-screen" data-testid="cashflow-projections-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-[#0F172A]" style={{fontFamily: 'Manrope'}}>
            {t.cashflowProjections}
          </h1>
          <p className="text-[#64748B]">
            {language === 'es' ? `Modelo Rolling 52 semanas | Inicio: ${DIAS_SEMANA_TR.find(d => d.value === companyConfig.inicio_semana)?.label || t.monday}` :
             language === 'en' ? `52-week Rolling Model | Start: ${DIAS_SEMANA_TR.find(d => d.value === companyConfig.inicio_semana)?.label || t.monday}` :
             `Modelo Rolling 52 semanas | Início: ${DIAS_SEMANA_TR.find(d => d.value === companyConfig.inicio_semana)?.label || t.monday}`}
            {selectedCurrency !== 'MXN' && (
              <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-sm">
                TC: 1 {selectedCurrency} = ${fxRates[selectedCurrency]?.toFixed(4) || '?'} MXN
              </span>
            )}
          </p>
          {/* Date range filter — past weeks hidden by default */}
          <div className="flex items-center gap-3 mt-2 flex-wrap">
            <div className="flex items-center gap-1.5">
              <Filter size={13} className="text-gray-400" />
              <span className="text-xs text-gray-500 font-medium">Rango de semanas:</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Label className="text-xs text-gray-400">Desde</Label>
              <Input
                type="date"
                value={filterFechaInicio}
                onChange={e => {
                  const val = e.target.value;
                  setFilterFechaInicio(val);
                  if (val) {
                    // Usar refs para datos siempre actualizados (evita stale state)
                    const weeks = processWeeklyData(
                      cfdisRef.current,
                      categoriesRef.current,
                      companyConfig.inicio_semana ?? 1,
                      fxRates,
                      paymentsRef.current,
                      val
                    );
                    setWeeklyData(applyBackendData(weeks, backendWeeksCache));
                  }
                }}
                className="h-7 w-36 text-xs"
              />
            </div>
            <div className="flex items-center gap-1.5">
              <Label className="text-xs text-gray-400">Hasta</Label>
              <Input
                type="date"
                value={filterFechaFin}
                onChange={e => setFilterFechaFin(e.target.value)}
                className="h-7 w-36 text-xs"
              />
            </div>
            {(filterFechaInicio || filterFechaFin) ? (
              <button
                className="text-xs text-blue-600 hover:underline"
                onClick={() => {
                  setFilterFechaInicio('');
                  setFilterFechaFin('');
                  const weeks = processWeeklyData(
                    cfdisRef.current,
                    categoriesRef.current,
                    companyConfig.inicio_semana ?? 1,
                    fxRates,
                    paymentsRef.current,
                    customStartDate
                  );
                  setWeeklyData(applyBackendData(weeks, backendWeeksCache));
                }}
              >
                Ver solo futuras
              </button>
            ) : null}
            <span className="text-xs text-gray-400">
              {displayedTotals.length} semana{displayedTotals.length !== 1 ? 's' : ''} visibles
              {!filterFechaInicio && ` (semanas pasadas ocultas)`}
            </span>
          </div>
        </div>
        <div className="flex gap-2">
          {/* Language Selector */}
          <Select value={language} onValueChange={handleLanguageChange}>
            <SelectTrigger className="w-36" data-testid="language-selector">
              <Globe className="w-4 h-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {languages.map(lang => (
                <SelectItem key={lang.code} value={lang.code}>
                  {lang.flag} {lang.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          {/* Config Dialog for Week Start */}
          <Dialog open={configDialogOpen} onOpenChange={setConfigDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="gap-2" data-testid="config-week-start-btn">
                <Settings size={16} />
                {t.configure}
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{t.configureProjections}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>{t.weekStartDay}</Label>
                  <Select 
                    value={companyConfig.inicio_semana?.toString()} 
                    onValueChange={(v) => handleSaveWeekStart(parseInt(v))}
                  >
                    <SelectTrigger data-testid="week-start-select">
                      <SelectValue placeholder={t.selectPeriod} />
                    </SelectTrigger>
                    <SelectContent>
                      {DIAS_SEMANA_TR.map(dia => (
                        <SelectItem key={dia.value} value={dia.value.toString()}>
                          {dia.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2 mt-4 pt-4 border-t">
                  <Label htmlFor="cashflow-start-date">
                    {language === 'es' ? 'Fecha de inicio del flujo' : language === 'pt' ? 'Data de início do fluxo' : 'Cashflow start date'}
                  </Label>
                  
                  {/* Quick preset buttons */}
                  <div className="flex flex-wrap gap-2 pb-1">
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      className="h-7 text-xs"
                      data-testid="cashflow-preset-current-year"
                      onClick={() => {
                        const d = new Date(new Date().getFullYear(), 0, 1);
                        handleSaveCustomStartDate(d.toISOString().slice(0, 10));
                      }}
                    >
                      {language === 'es' ? 'Año Actual' : language === 'pt' ? 'Ano Atual' : 'Current Year'}
                    </Button>
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      className="h-7 text-xs"
                      data-testid="cashflow-preset-fiscal-year"
                      title={language === 'es' ? 'Año fiscal cerrado anterior (Ene 1 año anterior)' : language === 'pt' ? 'Ano fiscal anterior fechado' : 'Previous closed fiscal year'}
                      onClick={() => {
                        const d = new Date(new Date().getFullYear() - 1, 0, 1);
                        handleSaveCustomStartDate(d.toISOString().slice(0, 10));
                      }}
                    >
                      {language === 'es' ? 'Año Fiscal' : language === 'pt' ? 'Ano Fiscal' : 'Fiscal Year'}
                    </Button>
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      className="h-7 text-xs"
                      data-testid="cashflow-preset-last-6-months"
                      onClick={() => {
                        const today = new Date();
                        const d = new Date(today.getFullYear(), today.getMonth() - 6, 1);
                        handleSaveCustomStartDate(d.toISOString().slice(0, 10));
                      }}
                    >
                      {language === 'es' ? 'Últimos 6 meses' : language === 'pt' ? 'Últimos 6 meses' : 'Last 6 months'}
                    </Button>
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      className="h-7 text-xs"
                      data-testid="cashflow-preset-last-12-months"
                      onClick={() => {
                        const today = new Date();
                        const d = new Date(today.getFullYear() - 1, today.getMonth(), 1);
                        handleSaveCustomStartDate(d.toISOString().slice(0, 10));
                      }}
                    >
                      {language === 'es' ? 'Últimos 12 meses' : language === 'pt' ? 'Últimos 12 meses' : 'Last 12 months'}
                    </Button>
                  </div>
                  
                  <div className="flex gap-2">
                    <Input
                      id="cashflow-start-date"
                      data-testid="cashflow-start-date-input"
                      type="date"
                      value={customStartDate}
                      onChange={(e) => handleSaveCustomStartDate(e.target.value)}
                    />
                    {customStartDate && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        data-testid="cashflow-start-date-reset"
                        onClick={() => handleSaveCustomStartDate('')}
                      >
                        {language === 'es' ? 'Auto' : language === 'pt' ? 'Auto' : 'Auto'}
                      </Button>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {language === 'es'
                      ? 'Elige cualquier fecha (ej. 5 ene 2026). Las 18 semanas se generarán a partir del día de inicio que configuraste arriba. Deja vacío para automático.'
                      : language === 'pt'
                        ? 'Escolha qualquer data (ex. 5 jan 2026). As 18 semanas começarão a partir do dia configurado acima. Deixe em branco para automático.'
                        : 'Pick any date (e.g. Jan 5, 2026). The 18 weeks will start from the configured weekday above. Leave empty for automatic.'}
                  </p>
                </div>
                <div className="space-y-2 mt-4 pt-4 border-t">
                  <Label>{t.minimumCashThreshold}</Label>
                  <Input 
                    type="number"
                    value={umbralMinimoCaja}
                    onChange={(e) => updateUmbralMinimoCaja(e.target.value)}
                    placeholder="Ej: 500000"
                  />
                </div>
              </div>
            </DialogContent>
          </Dialog>
          
          {/* Currency Selector */}
          <Select value={selectedCurrency} onValueChange={setSelectedCurrency}>
            <SelectTrigger className="w-32" data-testid="currency-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CURRENCIES.map(c => (
                <SelectItem key={c.code} value={c.code}>
                  {c.symbol} {c.code}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Button 
            variant="outline" 
            className="gap-2"
            onClick={exportProjectionsToExcel}
            data-testid="export-projections-btn"
          >
            <Download size={16} />
            Exportar Excel
          </Button>
          
          <Button
            variant="outline"
            className="no-print gap-2 border-red-200 text-red-700 hover:bg-red-50"
            onClick={exportToPDF}
            disabled={exportingPdf}
            data-testid="export-pdf-btn"
          >
            <FileDown size={16} />
            {exportingPdf ? 'Generando...' : t.exportPdf}
          </Button>
          
          <Dialog open={conceptDialogOpen} onOpenChange={setConceptDialogOpen}>
            <DialogTrigger asChild>
              <Button className="gap-2 bg-[#1B1740]" data-testid="add-concept-btn">
                <Plus size={16} />
                {t.addConcept}
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{t.addManualConcept}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>{t.conceptName}</Label>
                  <Input 
                    value={newConcept.nombre}
                    onChange={(e) => setNewConcept({...newConcept, nombre: e.target.value})}
                    placeholder={language === 'es' ? 'Ej: Nómina, Renta, Venta proyectada...' : 'E.g.: Payroll, Rent, Projected sale...'}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t.conceptType}</Label>
                    <Select value={newConcept.tipo} onValueChange={(v) => setNewConcept({...newConcept, tipo: v})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ingreso">
                          <span className="flex items-center gap-2"><TrendingUp size={14} className="text-green-500" /> {t.income}</span>
                        </SelectItem>
                        <SelectItem value="egreso">
                          <span className="flex items-center gap-2"><TrendingDown size={14} className="text-red-500" /> {t.expense}</span>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>{t.amount}</Label>
                    <Input 
                      type="number"
                      step="0.01"
                      value={newConcept.monto}
                      onChange={(e) => setNewConcept({...newConcept, monto: e.target.value})}
                      placeholder="0.00"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>{t.week}</Label>
                    <Select value={String(newConcept.semana)} onValueChange={(v) => setNewConcept({...newConcept, semana: parseInt(v)})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[1,2,3,4,5,6,7,8,9,10,11,12,13].map(s => (
                          <SelectItem key={s} value={String(s)}>{t.week} {s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2 flex items-end">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input 
                        type="checkbox" 
                        checked={newConcept.recurrente}
                        onChange={(e) => setNewConcept({...newConcept, recurrente: e.target.checked})}
                        className="w-4 h-4"
                      />
                      <span className="text-sm">{t.recurring} ({language === 'es' ? 'todas las semanas' : 'all weeks'})</span>
                    </label>
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setConceptDialogOpen(false)}>{t.cancel}</Button>
                <Button onClick={handleAddConcept}>{t.add}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Custom Concepts Summary */}
      {customConcepts.length > 0 && (
        <Card className="border-blue-200 bg-blue-50">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-blue-800">Conceptos Manuales Agregados ({customConcepts.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {customConcepts.map(c => (
                <div key={c.id} className={`flex items-center gap-2 px-3 py-1 rounded-full text-sm ${c.tipo === 'ingreso' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                  {c.tipo === 'ingreso' ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                  {c.nombre}: {formatCurrency(c.monto)}
                  {c.recurrente && <span className="text-xs">(Rec.)</span>}
                  <button onClick={() => handleDeleteConcept(c.id)} className="ml-1 hover:text-red-600">
                    <Trash2 size={12} />
                  </button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* View Mode Tabs */}
      <Tabs value={viewMode} onValueChange={setViewMode}>
        <TabsList>
          <TabsTrigger value="weekly" className="gap-2">
            <Calendar size={16} />
            Vista Semanal (52 semanas)
          </TabsTrigger>
          <TabsTrigger value="monthly" className="gap-2">
            <Calendar size={16} />
            Vista Mensual
          </TabsTrigger>
          <TabsTrigger value="byParty" className="gap-2">
            <Building2 size={16} />
            Por Cliente/Proveedor
          </TabsTrigger>
        </TabsList>

        {/* WEEKLY VIEW */}
        <TabsContent value="weekly" className="mt-4 space-y-4">
          <div ref={reportRef} id="cashflow-report-container">
          <div ref={kpiChartsRef} id="cashflow-kpi-charts">

          {/* ── PDF Header ──────────────────────────────────────────────── */}
          {/* Banner Pulse (fase 3): gradiente indigo de marca, antes navy plano */}
          <div
            className="text-white px-6 py-5 mb-4 rounded-xl flex items-center justify-between shadow-card"
            style={{ background: 'linear-gradient(120deg, #1B1740 0%, #35306B 55%, #4B44D6 130%)' }}
          >
            <div>
              <p className="text-xs font-semibold text-[#B9B4FF] uppercase tracking-widest mb-1">
                Proyección de Flujo de Efectivo · 18 Semanas Rolling
              </p>
              <h1 className="text-2xl font-bold tracking-tight">{companyConfig.nombre || 'Mi Empresa'}</h1>
              {companyConfig.rfc && (
                <p className="text-sm text-[#C7CBE3] mt-0.5">RFC: {companyConfig.rfc}</p>
              )}
            </div>
            <div className="text-right text-sm text-[#C7CBE3]">
              <p className="text-base font-semibold text-white/90">
                {format(new Date(), "MMMM yyyy", { locale: es })}
              </p>
              <p className="mt-1">Análisis: TaxnFin CFO Intelligence</p>
            </div>
          </div>

          {/* ===== CFO KPIs DASHBOARD ===== */}
          {cfoKPIs && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 mb-4" data-testid="cfo-kpis-section">
              {/* Net Burn Rate */}
              <div
                className="rounded-lg border bg-white shadow-sm border-l-4 border-l-blue-500 cursor-pointer hover:shadow-md transition-shadow"
                onClick={() => {
                  console.log('TEST INLINE CLICK — setKpiModal directo');
                  setKpiModal({ open: true, name: 'Net Burn Rate', formula: 'BurnRate = Σ(FlujoNeto) / N', description: 'Test directo', values: { 'Burn Rate Real': formatCurrency(cfoKPIs.burnRateReal) }, insight: '', loading: false, status: 'good', kpiKey: 'burnRate' });
                }}
                data-testid="kpi-burn-rate"
              >
                <div className="p-4 pt-3">
                  <div className="flex items-center justify-between text-sm text-gray-500 mb-1">
                    <div className="flex items-center gap-2"><Activity size={14} />Net Burn Rate</div>
                    <span className="text-xs text-blue-400">Ver análisis →</span>
                  </div>
                  <div className="space-y-1">
                    <div className="flex justify-between items-baseline">
                      <span className="text-xs text-gray-400">Real (promedio/sem):</span>
                      <span className={`text-lg font-bold ${cfoKPIs.burnRateReal >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {formatCurrency(cfoKPIs.burnRateReal)}/sem
                      </span>
                    </div>
                    <div className="flex justify-between items-baseline">
                      <span className="text-xs text-gray-400">Proyectado (promedio/sem):</span>
                      <span className={`text-lg font-bold ${cfoKPIs.burnRateProyectado >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {formatCurrency(cfoKPIs.burnRateProyectado)}/sem
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Cash Gap Analysis */}
              <div
                className={`rounded-lg border bg-white shadow-sm border-l-4 ${cfoKPIs.semanasEnRiesgo.length > 0 ? 'border-l-red-500' : 'border-l-green-500'} cursor-pointer hover:shadow-md transition-shadow`}
                onClick={() => openKpiModal('cashGap')}
                data-testid="kpi-cash-gap"
              >
                <div className="p-4 pt-3">
                  <div className="flex items-center justify-between text-sm text-gray-500 mb-1">
                    <div className="flex items-center gap-2"><AlertTriangle size={14} />Cash Gap Analysis</div>
                    <span className="text-xs text-blue-400">Ver análisis →</span>
                  </div>
                  <div className="space-y-1">
                    <div className="flex justify-between items-baseline">
                      <span className="text-xs text-gray-400">Umbral mínimo:</span>
                      <span className="text-sm font-medium">{formatCurrency(umbralMinimoCaja)}</span>
                    </div>
                    <div className="flex justify-between items-baseline">
                      <span className="text-xs text-gray-400">Semanas en riesgo:</span>
                      <span className={`text-lg font-bold ${cfoKPIs.semanasEnRiesgo.length > 0 ? 'text-red-600' : 'text-green-600'}`}>
                        {cfoKPIs.semanasEnRiesgo.length} visible{cfoKPIs.semanasEnRiesgo.length !== 1 ? 's' : ''}
                      </span>
                    </div>
                    {cfoKPIs.semanaCritica && (
                      <div className="flex justify-between items-baseline">
                        <span className="text-xs text-gray-400">Semana crítica:</span>
                        <span className="text-sm font-medium text-red-500">
                          {cfoKPIs.semanaCritica.semana} ({formatCurrency(cfoKPIs.semanaCritica.saldoFinal)})
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Volatilidad del Flujo */}
              <div
                className="rounded-lg border bg-white shadow-sm border-l-4 border-l-purple-500 cursor-pointer hover:shadow-md transition-shadow"
                onClick={() => openKpiModal('volatilidad')}
                data-testid="kpi-volatilidad"
              >
                <div className="p-4 pt-3">
                  <div className="flex items-center justify-between text-sm text-gray-500 mb-1">
                    <div className="flex items-center gap-2"><BarChart3 size={14} />Volatilidad del Flujo</div>
                    <span className="text-xs text-blue-400">Ver análisis →</span>
                  </div>
                  <div className="space-y-1">
                    <div className="flex justify-between items-baseline">
                      <span className="text-xs text-gray-400">Desv. Estándar (σ):</span>
                      <span className="text-lg font-bold text-purple-600">{formatCurrency(cfoKPIs.volatilidad)}</span>
                    </div>
                    <div className="flex justify-between items-baseline">
                      <span className="text-xs text-gray-400">Coef. Variación (CV):</span>
                      <span className={`text-sm font-medium ${cfoKPIs.coeficienteVariacion > 50 ? 'text-red-500' : cfoKPIs.coeficienteVariacion > 25 ? 'text-amber-500' : 'text-green-500'}`}>
                        {cfoKPIs.coeficienteVariacion.toFixed(1)}%
                      </span>
                    </div>
                    <p className="text-xs text-gray-400 mt-1">
                      {cfoKPIs.coeficienteVariacion > 50 ? '⚠️ Alta volatilidad' : cfoKPIs.coeficienteVariacion > 25 ? '⚡ Volatilidad moderada' : '✅ Flujo estable'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Indicadores Operativos */}
              <div
                className="rounded-lg border bg-white shadow-sm border-l-4 border-l-amber-500 cursor-pointer hover:shadow-md transition-shadow"
                onClick={() => openKpiModal('operativos')}
                data-testid="kpi-operativos"
              >
                <div className="p-4 pt-3">
                  <div className="flex items-center justify-between text-sm text-gray-500 mb-1">
                    <div className="flex items-center gap-2"><Target size={14} />Indicadores Operativos</div>
                    <span className="text-xs text-blue-400">Ver análisis →</span>
                  </div>
                  <div className="space-y-1">
                    <div className="flex justify-between items-baseline">
                      <span className="text-xs text-gray-400">Runway:</span>
                      <span className={`text-lg font-bold ${cfoKPIs.runway < 8 ? 'text-red-600' : cfoKPIs.runway < 16 ? 'text-amber-600' : 'text-green-600'}`}>
                        {cfoKPIs.runway > 100 ? '100+' : cfoKPIs.runway} semanas
                      </span>
                    </div>
                    <div className="flex justify-between items-baseline">
                      <span className="text-xs text-gray-400">Ratio Cobranza/Pagos:</span>
                      <span className={`text-lg font-bold ${cfoKPIs.ratioCobranzaPagos >= 1 ? 'text-green-600' : 'text-red-600'}`}>
                        {cfoKPIs.ratioCobranzaPagos > 10 ? '>10' : cfoKPIs.ratioCobranzaPagos.toFixed(2)}x
                      </span>
                    </div>
                    <p className="text-xs text-gray-400 mt-1">
                      {cfoKPIs.ratioCobranzaPagos >= 1 ? '✅ Cobranza > Pagos' : '⚠️ Pagos > Cobranza'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Runway (conservador / proyectado) */}
              {(() => {
                const { conservador, proyectado, avgNetBurn, horizonte } = runwayKPI;
                const semanasAMeses = (sem) => (sem / 4.33).toFixed(1);
                // Color del card según runway CONSERVADOR; caja positiva = verde
                const borderColor = conservador === null ? 'border-l-green-500'
                  : conservador > 12 ? 'border-l-green-500'
                  : conservador >= 6 ? 'border-l-amber-500'
                  : 'border-l-red-500';
                const consColor = conservador === null ? 'text-green-600'
                  : conservador > 12 ? 'text-green-600'
                  : conservador >= 6 ? 'text-amber-600'
                  : 'text-red-600';
                return (
                  <div
                    className={`rounded-lg border bg-white shadow-sm border-l-4 ${borderColor} hover:shadow-md transition-shadow`}
                    data-testid="kpi-runway"
                  >
                    <div className="p-4 pt-3">
                      <div className="flex items-center justify-between text-sm text-gray-500 mb-1">
                        <div className="flex items-center gap-2"><Calendar size={14} />Runway</div>
                      </div>
                      <div className="space-y-1">
                        <div className="flex justify-between items-baseline">
                          <span className="text-xs text-gray-400">Conservador:</span>
                          <span className={`text-lg font-bold ${consColor}`}>
                            {conservador === null
                              ? 'Caja positiva ✓'
                              : `${Math.round(conservador)} sem (${semanasAMeses(conservador)} meses)`}
                          </span>
                        </div>
                        <div className="flex justify-between items-baseline">
                          <span className="text-xs text-gray-400">Proyectado:</span>
                          <span className={`text-lg font-bold ${proyectado === null ? 'text-green-600' : proyectado > 12 ? 'text-green-600' : proyectado >= 6 ? 'text-amber-600' : 'text-red-600'}`}>
                            {proyectado === null
                              ? `>${horizonte} sem ✓`
                              : `${proyectado} sem (${semanasAMeses(proyectado)} meses)`}
                          </span>
                        </div>
                        <p className="text-xs text-gray-400 mt-1">
                          {conservador === null
                            ? `Net burn promedio 4 sem: ${formatCurrency(avgNetBurn)}/sem (genera caja)`
                            : `Net burn promedio 4 sem: ${formatCurrency(avgNetBurn)}/sem`}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
          )}

          {/* ===== FLUJO ACUMULADO RESUMEN ===== */}
          {cfoKPIs && (
            <Card className="bg-gradient-to-r from-slate-50 to-blue-50" data-testid="flujo-acumulado-section">
              <CardHeader className="py-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <TrendingUp size={18} />
                  Flujo de Caja Acumulado: Real vs Proyectado
                </CardTitle>
              </CardHeader>
              <CardContent className="py-2">
                {(() => {
                  const visReales = displayedTotals.filter(w => w.dataType === 'real' || w.dataType === 'actual');
                  const visProy   = displayedTotals.filter(w => w.dataType === 'proyectado');
                  const lbl = (arr) => arr.length === 0 ? '—'
                    : arr.length === 1 ? (arr[0].displayLabel || arr[0].label)
                    : `${arr[0].displayLabel || arr[0].label} – ${arr[arr.length-1].displayLabel || arr[arr.length-1].label}`;
                  return (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center p-3 bg-white rounded-lg shadow-sm">
                    <div className="text-xs text-gray-500 mb-1">Ingresos Reales ({lbl(visReales)})</div>
                    <div className="text-xl font-bold text-green-600">{formatCurrency(cfoKPIs.totalIngresosReales)}</div>
                  </div>
                  <div className="text-center p-3 bg-white rounded-lg shadow-sm">
                    <div className="text-xs text-gray-500 mb-1">Egresos Reales ({lbl(visReales)})</div>
                    <div className="text-xl font-bold text-red-600">{formatCurrency(cfoKPIs.totalEgresosReales)}</div>
                  </div>
                  <div className="text-center p-3 bg-white rounded-lg shadow-sm">
                    <div className="text-xs text-gray-500 mb-1">Ingresos Proyectados ({lbl(visProy)})</div>
                    <div className="text-xl font-bold text-green-500">{formatCurrency(cfoKPIs.totalIngresosProyectados)}</div>
                  </div>
                  <div className="text-center p-3 bg-white rounded-lg shadow-sm">
                    <div className="text-xs text-gray-500 mb-1">Egresos Proyectados ({lbl(visProy)})</div>
                    <div className="text-xl font-bold text-red-500">{formatCurrency(cfoKPIs.totalEgresosProyectados)}</div>
                  </div>
                </div>
                  );
                })()}
                <div className="mt-4 grid grid-cols-2 gap-4">
                  <div className="text-center p-4 bg-blue-100 rounded-lg">
                    <div className="text-sm text-blue-700 mb-1">Flujo Neto Real Acumulado</div>
                    <div className={`text-2xl font-bold ${cfoKPIs.totalFlujoNetoReal >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                      {formatCurrency(cfoKPIs.totalFlujoNetoReal)}
                    </div>
                  </div>
                  <div className="text-center p-4 bg-indigo-100 rounded-lg">
                    <div className="text-sm text-indigo-700 mb-1">Flujo Neto Proyectado Total</div>
                    <div className={`text-2xl font-bold ${cfoKPIs.acumuladoProyectadoFinal >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                      {formatCurrency(cfoKPIs.acumuladoProyectadoFinal)}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* ===== GRÁFICOS COMPARATIVOS ===== */}
          {chartData.length > 0 && (
            <div ref={chartsRef} className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="charts-section">
              {/* Gráfico 1: Flujo Acumulado Real vs Proyectado */}
              <Card>
                <CardHeader className="py-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <TrendingUp size={18} className="text-blue-600" />
                    Flujo Acumulado: Real vs Proyectado
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={250}>
                    <ComposedChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                      <XAxis dataKey="semana" tick={{ fontSize: 11 }} />
                      <YAxis 
                        tick={{ fontSize: 10 }} 
                        tickFormatter={(v) => `${(v / 1000000).toFixed(1)}M`}
                      />
                      <Tooltip 
                        formatter={(value, name) => [
                          `$${value?.toLocaleString('es-MX', { minimumFractionDigits: 0 })}`,
                          name === 'acumuladoReal' ? 'Real Acumulado' : 
                          name === 'acumuladoProyectado' ? 'Proyectado Total' : name
                        ]}
                        labelFormatter={(label) => `Semana ${label}`}
                      />
                      <Legend />
                      <Area 
                        type="monotone" 
                        dataKey="acumuladoReal" 
                        fill="#22c55e" 
                        fillOpacity={0.3}
                        stroke="#16a34a" 
                        strokeWidth={2}
                        name="Real Acumulado"
                        connectNulls={false}
                      />
                      <Line 
                        type="monotone" 
                        dataKey="acumuladoProyectado" 
                        stroke="#6366f1" 
                        strokeWidth={2}
                        strokeDasharray="5 5"
                        name="Proyectado Total"
                        dot={{ fill: '#6366f1', r: 3 }}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Gráfico 2: Ingresos vs Egresos por Semana */}
              <Card>
                <CardHeader className="py-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <BarChart3 size={18} className="text-purple-600" />
                    Ingresos vs Egresos Semanal
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                      <XAxis dataKey="semana" tick={{ fontSize: 11 }} />
                      <YAxis 
                        tick={{ fontSize: 10 }} 
                        tickFormatter={(v) => `${(v / 1000000).toFixed(1)}M`}
                      />
                      <Tooltip 
                        formatter={(value) => [`$${value?.toLocaleString('es-MX', { minimumFractionDigits: 0 })}`]}
                        labelFormatter={(label) => `Semana ${label}`}
                      />
                      <Legend />
                      <Bar dataKey="ingresos" fill="#22c55e" name="Ingresos" radius={[2, 2, 0, 0]} />
                      <Bar dataKey="egresos" fill="#ef4444" name="Egresos" radius={[2, 2, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Gráfico 3: Saldo Final vs Umbral Mínimo (Cash Gap) */}
              <Card>
                <CardHeader className="py-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <AlertTriangle size={18} className="text-amber-600" />
                    Saldo Final vs Umbral Mínimo (Cash Gap)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={250}>
                    <ComposedChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                      <XAxis dataKey="semana" tick={{ fontSize: 11 }} />
                      <YAxis 
                        tick={{ fontSize: 10 }} 
                        tickFormatter={(v) => `${(v / 1000000).toFixed(1)}M`}
                      />
                      <Tooltip 
                        formatter={(value, name) => [
                          `$${value?.toLocaleString('es-MX', { minimumFractionDigits: 0 })}`,
                          name === 'saldoFinal' ? 'Saldo Final' : 
                          name === 'umbralMinimo' ? 'Umbral Mínimo' : name
                        ]}
                        labelFormatter={(label) => `Semana ${label}`}
                      />
                      <Legend />
                      <Area 
                        type="monotone" 
                        dataKey="saldoFinal" 
                        fill="#3b82f6" 
                        fillOpacity={0.3}
                        stroke="#2563eb" 
                        strokeWidth={2}
                        name="Saldo Final"
                      />
                      <ReferenceLine 
                        y={chartData[0]?.umbralMinimo || 0} 
                        stroke="#ef4444" 
                        strokeWidth={2}
                        strokeDasharray="5 5"
                        label={{ value: 'Umbral Mínimo', fill: '#ef4444', fontSize: 10 }}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Gráfico 4: Flujo Neto Semanal */}
              <Card>
                <CardHeader className="py-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Activity size={18} className="text-indigo-600" />
                    Flujo Neto Semanal
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={250}>
                    <ComposedChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                      <XAxis dataKey="semana" tick={{ fontSize: 11 }} />
                      <YAxis 
                        tick={{ fontSize: 10 }} 
                        tickFormatter={(v) => `${(v / 1000000).toFixed(1)}M`}
                      />
                      <Tooltip 
                        formatter={(value) => [`$${value?.toLocaleString('es-MX', { minimumFractionDigits: 0 })}`]}
                        labelFormatter={(label) => `Semana ${label}`}
                      />
                      <Legend />
                      <ReferenceLine y={0} stroke="#666" strokeWidth={1} />
                      <Bar 
                        dataKey="flujoNetoPositivo" 
                        fill="#22c55e"
                        name="Flujo Positivo"
                        stackId="flujo"
                        radius={[2, 2, 0, 0]}
                      />
                      <Bar 
                        dataKey="flujoNetoNegativo" 
                        fill="#ef4444"
                        name="Flujo Negativo"
                        stackId="flujo"
                        radius={[2, 2, 0, 0]}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </div>
          )}

          </div>{/* end cashflow-kpi-charts */}

          {/* ===== MAIN CASH FLOW TABLE ===== */}
          <Card ref={tableModelRef} id="cashflow-table-model" data-table-model="true">
            <CardHeader className="bg-[#1B1740] text-white rounded-t-lg">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-3">
                  <span>Modelo de Flujo de Efectivo - 18 Semanas</span>
                  <div className="flex items-center gap-2 ml-4">
                    <Button
                      variant={tableViewMode === 'categoria' ? 'secondary' : 'ghost'}
                      size="sm"
                      className={`h-7 text-xs ${tableViewMode === 'categoria' ? 'bg-white text-[#0F172A]' : 'text-white/70 hover:text-white hover:bg-white/10'}`}
                      onClick={() => setTableViewMode('categoria')}
                    >
                      <Layers size={14} className="mr-1" />
                      Por Categoría
                    </Button>
                    <Button
                      variant={tableViewMode === 'tercero' ? 'secondary' : 'ghost'}
                      size="sm"
                      className={`h-7 text-xs ${tableViewMode === 'tercero' ? 'bg-white text-[#0F172A]' : 'text-white/70 hover:text-white hover:bg-white/10'}`}
                      onClick={() => setTableViewMode('tercero')}
                    >
                      <Building2 size={14} className="mr-1" />
                      Por Proveedor/Cliente
                    </Button>
                  </div>
                </CardTitle>
                <div className="flex items-center gap-3">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 text-xs text-white/80 hover:text-white hover:bg-white/10 gap-1"
                    onClick={exportDetailReport}
                  >
                    <FileSpreadsheet size={14} />
                    Exportar Detalle
                  </Button>
                  <span className="text-sm font-normal opacity-70">
                    {format(new Date(), 'MMMM yyyy', { locale: es })}
                  </span>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto [&_td]:py-1 [&_td]:px-2 [&_th]:py-1.5 [&_th]:px-2">
                <Table className="text-xs">
                  <TableHeader>
                    <TableRow className="bg-gray-100">
                      <TableHead className="sticky left-0 bg-gray-100 min-w-[200px] font-bold">CONCEPTO</TableHead>
                      {weeklyTotals.map((week, idx) => columnVisible[idx] ? (
                        <TableHead key={idx} className={`text-center min-w-[65px] ${
                          week.dataType === 'real' ? 'bg-yellow-50' :
                          week.dataType === 'actual' ? 'bg-blue-50' : 'bg-gray-50'
                        }`}>
                          <div className="font-bold">{week.displayLabel || week.label}</div>
                          <div className="text-xs text-gray-500">{week.dateLabel}</div>
                          <div className={`text-xs font-semibold ${
                            week.dataType === 'real' ? 'text-yellow-600' :
                            week.dataType === 'actual' ? 'text-blue-600' : 'text-gray-400'
                          }`}>
                            {week.dataType === 'real' ? 'Real' : week.dataType === 'actual' ? 'Actual' : 'Proy'}
                          </div>
                        </TableHead>
                      ) : null)}
                      <TableHead className="text-center min-w-[120px] bg-blue-50 font-bold">TOTAL</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {tableViewMode === 'categoria' ? (
                      <>
                        {/* SALDO INICIAL POR SEMANA Row */}
                        <TableRow className="bg-blue-100 font-bold border-b-2 border-blue-300">
                          <TableCell className="sticky left-0 bg-blue-100">
                            <div className="flex items-center gap-2">
                              <Building2 className="text-blue-600" size={16} />
                              SALDO INICIAL SEMANA
                            </div>
                          </TableCell>
                          {weeklyTotals.map((week, idx) => columnVisible[idx] ? (
                            <TableCell
                              key={idx}
                              className="text-center text-blue-700 font-bold text-sm"
                            >
                              {formatCurrency(week.saldoInicial)}
                            </TableCell>
                          ) : null)}
                          <TableCell className="text-center bg-blue-200 text-blue-800 font-bold">
                            {formatCurrency(displayedTotals[0]?.saldoInicial || saldoInicialBancos)}
                          </TableCell>
                        </TableRow>

                        {/* RECEIPTS / INGRESOS Section */}
                    <TableRow className="bg-green-50 font-bold">
                      <TableCell className="sticky left-0 bg-green-50">
                        <div className="flex items-center gap-2">
                          <TrendingUp className="text-green-600" size={16} />
                          INGRESOS
                          {displayedTotals.some(w => w.dataType === 'real' || w.dataType === 'actual') && (
                            <span className="text-xs px-2 py-0.5 bg-green-200 text-green-800 rounded">S1-S5 Real</span>
                          )}
                        </div>
                      </TableCell>
                      {weeklyTotals.map((week, idx) => columnVisible[idx] ? (
                        <TableCell key={idx} className={`text-center font-bold text-sm ${
                          week.dataType === 'real' ? 'text-green-800 bg-green-100' :
                          week.dataType === 'actual' ? 'text-green-700 bg-green-50' : 'text-green-600'
                        }`}>
                          {formatCurrency(week.ingresos.total)}
                        </TableCell>
                      ) : null)}
                      <TableCell className="text-center bg-green-100 text-green-800 font-bold">
                        {formatCurrency(grandTotalIngresos)}
                      </TableCell>
                    </TableRow>

                    {/* Ingresos by Category - Show "Cobranza" or category name, also show "Sin categoría" */}
                    {/* Exclude "Compra de USD" category as it's shown separately */}
                    {(() => {
                      // Collect all unique category names from ingresos including "Sin categoría"
                      const allIngresoCategories = new Set();
                      weeklyTotals.forEach(w => {
                        Object.keys(w.ingresos.byCategory).forEach(cat => {
                          // Exclude USD operations from INGRESOS section
                          if (!cat.toLowerCase().includes('compra de usd') && !cat.toLowerCase().includes('compra usd')) {
                            allIngresoCategories.add(cat);
                          }
                        });
                      });

                      return Array.from(allIngresoCategories).map((categoryName, catIdx) => {
                        const categoryKey = `ing-${categoryName}`;
                        const isExpanded = expandedRows[categoryKey];
                        const weekTotals = weeklyTotals.map(w => w.ingresos.byCategory[categoryName]?.total || 0);
                        const categoryTotal = weekTotals.reduce((sum, t) => sum + t, 0);

                        if (categoryTotal === 0) return null;

                        return (
                          <React.Fragment key={categoryKey}>
                            <TableRow className={`${catIdx % 2 === 1 ? 'bg-slate-50' : 'bg-white'} hover:bg-green-50/50`}>
                              <TableCell className="sticky left-0 bg-white pl-8">
                                <button 
                                  onClick={() => toggleRow(categoryKey)}
                                  className="flex items-center gap-1 text-gray-700 hover:text-green-600"
                                >
                                  {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                                  {categoryName === 'Sin categoría' ? 'Cobranza' : categoryName}
                                </button>
                              </TableCell>
                              {weekTotals.map((total, idx) => columnVisible[idx] ? (
                                <TableCell
                                  key={idx}
                                  className={`text-center text-green-600 ${total > 0 ? 'cursor-pointer hover:bg-green-100 hover:underline' : ''}`}
                                  onClick={() => total > 0 && handleCellClick(idx, 'ingreso', categoryName)}
                                >
                                  {total > 0 ? formatCurrency(total) : '-'}
                                </TableCell>
                              ) : null)}
                              <TableCell className="text-center bg-green-50 text-green-700">
                                {formatCurrency(categoryTotal)}
                              </TableCell>
                            </TableRow>
                            {/* Subcategorías expandibles */}
                            {isExpanded && (() => {
                              const allSubcategories = new Set();
                              weeklyTotals.forEach(w => {
                                const cat = w.ingresos.byCategory[categoryName];
                                if (cat?.bySubcategory) {
                                  Object.keys(cat.bySubcategory).forEach(sub => allSubcategories.add(sub));
                                }
                              });
                              
                              return Array.from(allSubcategories).map(subName => {
                                const subTotals = weeklyTotals.map(w =>
                                  w.ingresos.byCategory[categoryName]?.bySubcategory?.[subName]?.total || 0
                                );
                                const subTotal = subTotals.reduce((s, t) => s + t, 0);
                                if (subTotal === 0) return null;
                                
                                return (
                                  <TableRow key={`${categoryKey}-${subName}`} className="bg-green-50/30">
                                    <TableCell className="sticky left-0 bg-green-50/30 pl-14 text-sm text-gray-600">
                                      └ {subName}
                                    </TableCell>
                                    {subTotals.map((total, idx) => columnVisible[idx] ? (
                                      <TableCell
                                        key={idx}
                                        className={`text-center text-green-500 text-sm ${total > 0 ? 'cursor-pointer hover:bg-green-100 hover:underline' : ''}`}
                                        onClick={() => total > 0 && handleCellClick(idx, 'ingreso', categoryName, subName)}
                                      >
                                        {total > 0 ? formatCurrency(total) : '-'}
                                      </TableCell>
                                    ) : null)}
                                    <TableCell className="text-center bg-green-50 text-green-600 text-sm">
                                      {formatCurrency(subTotal)}
                                    </TableCell>
                                  </TableRow>
                                );
                              });
                            })()}
                          </React.Fragment>
                        );
                      });
                    })()}

                    {/* OPERATING DISBURSEMENTS / EGRESOS Section */}
                    <TableRow className="bg-red-50 font-bold">
                      <TableCell className="sticky left-0 bg-red-50">
                        <div className="flex items-center gap-2">
                          <TrendingDown className="text-red-600" size={16} />
                          EGRESOS
                          {displayedTotals.some(w => w.dataType === 'real' || w.dataType === 'actual') && (
                            <span className="text-xs px-2 py-0.5 bg-red-200 text-red-800 rounded">S1-S5 Real</span>
                          )}
                        </div>
                      </TableCell>
                      {weeklyTotals.map((week, idx) => columnVisible[idx] ? (
                        <TableCell key={idx} className={`text-center font-bold text-sm ${
                          week.dataType === 'real' ? 'text-red-800 bg-red-100' :
                          week.dataType === 'actual' ? 'text-red-700 bg-red-50' : 'text-red-600'
                        }`}>
                          {formatCurrency(week.egresos.total)}
                        </TableCell>
                      ) : null)}
                      <TableCell className="text-center bg-red-100 text-red-800 font-bold">
                        {formatCurrency(grandTotalEgresos)}
                      </TableCell>
                    </TableRow>
                    
                    {/* Egresos by Category - Show all including "Sin categoría" as "Proveedores Costo" */}
                    {/* Exclude "Venta de USD" category as it's shown separately */}
                    {(() => {
                      // Collect all unique category names from egresos including "Sin categoría"
                      const allEgresoCategories = new Set();
                      weeklyTotals.forEach(w => {
                        Object.keys(w.egresos.byCategory).forEach(cat => {
                          // Exclude USD operations from EGRESOS section
                          if (!cat.toLowerCase().includes('venta de usd') && !cat.toLowerCase().includes('venta usd')) {
                            allEgresoCategories.add(cat);
                          }
                        });
                      });

                      return Array.from(allEgresoCategories).map((categoryName, catIdx) => {
                        const categoryKey = `egr-${categoryName}`;
                        const isExpanded = expandedRows[categoryKey];
                        const weekTotals = weeklyTotals.map(w => w.egresos.byCategory[categoryName]?.total || 0);
                        const categoryTotal = weekTotals.reduce((sum, t) => sum + t, 0);

                        if (categoryTotal === 0) return null;

                        return (
                          <React.Fragment key={categoryKey}>
                            <TableRow className={`${catIdx % 2 === 1 ? 'bg-slate-50' : 'bg-white'} hover:bg-red-50/50`}>
                              <TableCell className="sticky left-0 bg-white pl-8">
                                <button 
                                  onClick={() => toggleRow(categoryKey)}
                                  className="flex items-center gap-1 text-gray-700 hover:text-red-600"
                                >
                                  {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                                  {categoryName === 'Sin categoría' ? 'Proveedores Costo' : categoryName}
                                </button>
                              </TableCell>
                              {weekTotals.map((total, idx) => columnVisible[idx] ? (
                                <TableCell
                                  key={idx}
                                  className={`text-center text-red-600 ${total > 0 ? 'cursor-pointer hover:bg-red-100 hover:underline' : ''}`}
                                  onClick={() => total > 0 && handleCellClick(idx, 'egreso', categoryName)}
                                >
                                  {total > 0 ? formatCurrency(total) : '-'}
                                </TableCell>
                              ) : null)}
                              <TableCell className="text-center bg-red-50 text-red-700">
                                {formatCurrency(categoryTotal)}
                              </TableCell>
                            </TableRow>
                            {/* Subcategorías expandibles */}
                            {isExpanded && (() => {
                              const allSubcategories = new Set();
                              weeklyTotals.forEach(w => {
                                const cat = w.egresos.byCategory[categoryName];
                                if (cat?.bySubcategory) {
                                  Object.keys(cat.bySubcategory).forEach(sub => allSubcategories.add(sub));
                                }
                              });
                              
                              return Array.from(allSubcategories).map(subName => {
                                const subTotals = weeklyTotals.map(w =>
                                  w.egresos.byCategory[categoryName]?.bySubcategory?.[subName]?.total || 0
                                );
                                const subTotal = subTotals.reduce((s, t) => s + t, 0);
                                if (subTotal === 0) return null;
                                
                                return (
                                  <TableRow key={`${categoryKey}-${subName}`} className="bg-red-50/30">
                                    <TableCell className="sticky left-0 bg-red-50/30 pl-14 text-sm text-gray-600">
                                      └ {subName}
                                    </TableCell>
                                    {subTotals.map((total, idx) => columnVisible[idx] ? (
                                      <TableCell
                                        key={idx}
                                        className={`text-center text-red-500 text-sm ${total > 0 ? 'cursor-pointer hover:bg-red-100 hover:underline' : ''}`}
                                        onClick={() => total > 0 && handleCellClick(idx, 'egreso', categoryName, subName)}
                                      >
                                        {total > 0 ? formatCurrency(total) : '-'}
                                      </TableCell>
                                    ) : null)}
                                    <TableCell className="text-center bg-red-50 text-red-600 text-sm">
                                      {formatCurrency(subTotal)}
                                    </TableCell>
                                  </TableRow>
                                );
                              });
                            })()}
                          </React.Fragment>
                        );
                      });
                    })()}

                    {/* COMPRA/VENTA DE DIVISAS Section - Separate from Income/Expenses */}
                    {(grandTotalCompraUSD > 0 || grandTotalVentaUSD > 0) && (
                      <>
                        <TableRow className="bg-purple-50 font-bold border-t-2 border-purple-200">
                          <TableCell className="sticky left-0 bg-purple-50">
                            <div className="flex items-center gap-2">
                              <span className="text-purple-600">💱</span>
                              OPERACIONES CON DIVISAS
                            </div>
                          </TableCell>
                          {weeklyTotals.map((week, idx) => columnVisible[idx] ? (
                            <TableCell key={idx} className={`text-center font-bold ${(week.flujoDivisas || 0) >= 0 ? 'text-purple-700' : 'text-purple-700'}`}>
                              {formatCurrency(week.flujoDivisas || 0)}
                            </TableCell>
                          ) : null)}
                          <TableCell className="text-center bg-purple-100 text-purple-800 font-bold">
                            {formatCurrency(grandTotalFlujoDivisas)}
                          </TableCell>
                        </TableRow>
                        
                        {/* Venta de USD row - SIEMPRE MOSTRAR */}
                        <TableRow className="hover:bg-purple-50/50">
                          <TableCell className="sticky left-0 bg-white pl-8">
                            <div className="flex items-center gap-1 text-green-600">
                              <TrendingUp size={14} />
                              Venta de USD (entrada MXN)
                            </div>
                          </TableCell>
                          {weeklyTotals.map((week, idx) => columnVisible[idx] ? (
                            <TableCell key={idx} className="text-center text-green-600">
                              {(week.ventaUSD || 0) > 0 ? formatCurrency(week.ventaUSD) : '-'}
                            </TableCell>
                          ) : null)}
                          <TableCell className="text-center bg-green-50 text-green-700">
                            {formatCurrency(grandTotalVentaUSD)}
                          </TableCell>
                        </TableRow>
                        
                        {/* Compra de USD row - SIEMPRE MOSTRAR */}
                        <TableRow className="hover:bg-purple-50/50">
                          <TableCell className="sticky left-0 bg-white pl-8">
                            <div className="flex items-center gap-1 text-red-600">
                              <TrendingDown size={14} />
                              Compra de USD (salida MXN)
                            </div>
                          </TableCell>
                          {weeklyTotals.map((week, idx) => columnVisible[idx] ? (
                            <TableCell key={idx} className="text-center text-red-600">
                              {(week.compraUSD || 0) > 0 ? `(${formatCurrency(week.compraUSD)})` : '-'}
                            </TableCell>
                          ) : null)}
                          <TableCell className="text-center bg-red-50 text-red-700">
                            {grandTotalCompraUSD > 0 ? `(${formatCurrency(grandTotalCompraUSD)})` : '$0.00'}
                          </TableCell>
                        </TableRow>
                      </>
                    )}

                    {/* SIN CLASIFICAR — movimientos con categoría genérica */}
                    {(() => {
                      // Recolectar items sin_clasificar de ingresos y egresos
                      const sinClasIng = weeklyTotals.map(w =>
                        (w.backend_ingresos_detalle || []).filter(i => i.sin_clasificar)
                      );
                      const sinClasEgr = weeklyTotals.map(w =>
                        (w.backend_egresos_detalle || []).filter(i => i.sin_clasificar)
                      );
                      const totalSinClasIng = sinClasIng.reduce((s, arr) => s + arr.reduce((a, i) => a + i.monto, 0), 0);
                      const totalSinClasEgr = sinClasEgr.reduce((s, arr) => s + arr.reduce((a, i) => a + i.monto, 0), 0);
                      if (totalSinClasIng === 0 && totalSinClasEgr === 0) return null;
                      return (
                        <>
                          <TableRow className="bg-amber-50 font-semibold border-t-2 border-amber-300">
                            <TableCell className="sticky left-0 bg-amber-50">
                              <div className="flex items-center gap-2 text-amber-700">
                                <AlertTriangle size={14} />
                                SIN CLASIFICAR
                                <span className="text-xs font-normal text-amber-500">(haz clic para clasificar)</span>
                              </div>
                            </TableCell>
                            {weeklyTotals.map((_, idx) => columnVisible[idx] ? (
                              <TableCell key={idx} className="text-center text-amber-600 text-xs">
                                {(sinClasIng[idx]?.length > 0 || sinClasEgr[idx]?.length > 0)
                                  ? `${sinClasIng[idx]?.length + sinClasEgr[idx]?.length} mov.` : '-'}
                              </TableCell>
                            ) : null)}
                            <TableCell className="text-center bg-amber-100 text-amber-700 text-xs">
                              {totalSinClasIng + totalSinClasEgr > 0
                                ? `${sinClasIng.flat().length + sinClasEgr.flat().length} mov.` : '-'}
                            </TableCell>
                          </TableRow>
                          {/* Sublínea ingresos sin clasificar */}
                          {totalSinClasIng > 0 && (
                            <TableRow className="hover:bg-amber-50/50">
                              <TableCell className="sticky left-0 bg-white pl-8 text-amber-600 text-xs">
                                ↑ Cobros sin clasificar
                              </TableCell>
                              {weeklyTotals.map((_, idx) => {
                                if (!columnVisible[idx]) return null;
                                const items = sinClasIng[idx] || [];
                                const tot = items.reduce((s, i) => s + i.monto, 0);
                                return (
                                  <TableCell key={idx} className="text-center text-xs">
                                    {items.map((item, ii) => (
                                      <div key={ii} className="flex items-center justify-center gap-1">
                                        <span className="text-green-600">{formatCurrency(item.monto)}</span>
                                        <button
                                          className="text-amber-500 hover:text-amber-700 text-xs underline"
                                          onClick={() => setClasificarModal({ open: true, item, nuevaCategoria: '' })}
                                          title="Clasificar"
                                        >✎</button>
                                      </div>
                                    ))}
                                    {items.length === 0 && '-'}
                                  </TableCell>
                                );
                              })}
                              <TableCell className="text-center bg-amber-50 text-green-600 text-xs font-medium">
                                {formatCurrency(totalSinClasIng)}
                              </TableCell>
                            </TableRow>
                          )}
                          {/* Sublínea egresos sin clasificar */}
                          {totalSinClasEgr > 0 && (
                            <TableRow className="hover:bg-amber-50/50">
                              <TableCell className="sticky left-0 bg-white pl-8 text-amber-600 text-xs">
                                ↓ Pagos sin clasificar
                              </TableCell>
                              {weeklyTotals.map((_, idx) => {
                                if (!columnVisible[idx]) return null;
                                const items = sinClasEgr[idx] || [];
                                return (
                                  <TableCell key={idx} className="text-center text-xs">
                                    {items.map((item, ii) => (
                                      <div key={ii} className="flex items-center justify-center gap-1">
                                        <span className="text-red-600">{formatCurrency(item.monto)}</span>
                                        <button
                                          className="text-amber-500 hover:text-amber-700 text-xs underline"
                                          onClick={() => setClasificarModal({ open: true, item, nuevaCategoria: '' })}
                                          title="Clasificar"
                                        >✎</button>
                                      </div>
                                    ))}
                                    {items.length === 0 && '-'}
                                  </TableCell>
                                );
                              })}
                              <TableCell className="text-center bg-amber-50 text-red-600 text-xs font-medium">
                                {formatCurrency(totalSinClasEgr)}
                              </TableCell>
                            </TableRow>
                          )}
                        </>
                      );
                    })()}

                    {/* TRASPASOS ENTRE CUENTAS — fila informativa, neta $0 */}
                    {(() => {
                      const grandTrasIng = weeklyTotals.reduce((s, w) => s + (w.traspasos_ingreso || 0), 0);
                      const grandTrasEgr = weeklyTotals.reduce((s, w) => s + (w.traspasos_egreso || 0), 0);
                      if (grandTrasIng === 0 && grandTrasEgr === 0) return null;
                      return (
                        <TableRow className="bg-purple-50/50 border-t border-purple-200">
                          <TableCell className="sticky left-0 bg-purple-50/50">
                            <div className="flex items-center gap-2 text-purple-600 text-sm">
                              <span>↔</span> Traspasos entre cuentas
                              <span className="text-xs font-normal text-purple-400">(informativo, no afecta saldo)</span>
                            </div>
                          </TableCell>
                          {weeklyTotals.map((week, idx) => {
                            if (!columnVisible[idx]) return null;
                            const ing = week.traspasos_ingreso || 0;
                            const egr = week.traspasos_egreso || 0;
                            if (ing === 0 && egr === 0) return <TableCell key={idx} className="text-center text-xs">-</TableCell>;
                            return (
                              <TableCell key={idx} className="text-center text-xs text-purple-600">
                                {ing > 0 && <div>↑{formatCurrency(ing)}</div>}
                                {egr > 0 && <div>↓{formatCurrency(egr)}</div>}
                              </TableCell>
                            );
                          })}
                          <TableCell className="text-center bg-purple-50 text-purple-700 text-xs">
                            ↑{formatCurrency(grandTrasIng)} / ↓{formatCurrency(grandTrasEgr)}
                          </TableCell>
                        </TableRow>
                      );
                    })()}

                    {/* Ajuste bancario eliminado: la fórmula rolling SF(Sn)=SI(Sn+1) es suficiente */}

                    {/* NET CASH FLOW */}
                    <TableRow className="bg-blue-100 font-bold border-t-2 border-blue-300">
                      <TableCell className="sticky left-0 bg-blue-100">
                        FLUJO NETO
                      </TableCell>
                      {weeklyTotals.map((week, idx) => columnVisible[idx] ? (
                        <TableCell
                          key={idx}
                          className={`text-center font-bold ${week.flujoNeto >= 0 ? 'text-green-700' : 'text-red-700'}`}
                        >
                          {formatCurrency(week.flujoNeto)}
                        </TableCell>
                      ) : null)}
                      <TableCell className={`text-center font-bold ${grandTotalFlujo >= 0 ? 'text-green-800 bg-green-100' : 'text-red-800 bg-red-100'}`}>
                        {formatCurrency(grandTotalFlujo)}
                      </TableCell>
                    </TableRow>

                    {/* SALDO FINAL POR SEMANA */}
                    <TableRow className="bg-[#1B1740] text-white font-bold">
                      <TableCell className="sticky left-0 bg-[#1B1740]">
                        SALDO FINAL SEMANA
                      </TableCell>
                      {weeklyTotals.map((week, idx) => columnVisible[idx] ? (
                        <TableCell
                          key={idx}
                          className={`text-center font-bold text-sm ${week.saldoFinal >= 0 ? 'text-green-400' : 'text-red-400'}`}
                        >
                          {formatCurrency(week.saldoFinal)}
                        </TableCell>
                      ) : null)}
                      <TableCell className="text-center font-bold">
                        {formatCurrency(displayedTotals[displayedTotals.length - 1]?.saldoFinal || 0)}
                      </TableCell>
                    </TableRow>

                    {/* CASH GAP - Diferencia vs Umbral Mínimo */}
                    <TableRow className="bg-amber-50 font-medium">
                      <TableCell className="sticky left-0 bg-amber-50">
                        <div className="flex items-center gap-2">
                          <AlertTriangle className="text-amber-600" size={14} />
                          CASH GAP (vs {formatCurrency(umbralMinimoCaja)})
                        </div>
                      </TableCell>
                      {weeklyTotals.map((week, idx) => {
                        if (!columnVisible[idx]) return null;
                        const gap = week.saldoFinal - umbralMinimoCaja;
                        const isNegative = gap < 0;
                        return (
                          <TableCell
                            key={idx}
                            className={`text-center text-sm ${isNegative ? 'text-red-600 bg-red-50 font-bold' : 'text-green-600'}`}
                          >
                            {isNegative ? `(${formatCurrency(Math.abs(gap))})` : formatCurrency(gap)}
                          </TableCell>
                        );
                      })}
                      <TableCell className="text-center bg-amber-100 text-amber-800 font-bold">
                        {(() => {
                          const finalGap = (displayedTotals[displayedTotals.length - 1]?.saldoFinal || 0) - umbralMinimoCaja;
                          return finalGap < 0 ? `(${formatCurrency(Math.abs(finalGap))})` : formatCurrency(finalGap);
                        })()}
                      </TableCell>
                    </TableRow>
                      </>
                    ) : (
                      /* ===== VISTA POR PROVEEDOR/CLIENTE ===== */
                      <>
                        {/* Filters Row */}
                        <TableRow className="bg-blue-50 border-b-2 border-blue-200">
                          <TableCell colSpan={weeklyTotals.length + 2} className="py-3">
                            <div className="flex items-center gap-4 flex-wrap">
                              <div className="flex items-center gap-2">
                                <Filter size={16} className="text-blue-600" />
                                <span className="text-sm font-medium text-blue-800">Filtros:</span>
                              </div>
                              
                              {/* Search by name */}
                              <div className="relative flex-1 min-w-[200px] max-w-[300px]">
                                <Search size={14} className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400" />
                                <Input
                                  placeholder="Buscar proveedor/cliente..."
                                  value={partyFilters.searchTerm}
                                  onChange={(e) => setPartyFilters(prev => ({ ...prev, searchTerm: e.target.value }))}
                                  className="pl-8 h-8 text-sm"
                                  data-testid="party-filter-search"
                                />
                              </div>
                              
                              {/* Filter by tipo */}
                              <Select
                                value={partyFilters.tipoTercero}
                                onValueChange={(value) => setPartyFilters(prev => ({ ...prev, tipoTercero: value }))}
                              >
                                <SelectTrigger className="w-[140px] h-8 text-sm" data-testid="party-filter-type">
                                  <SelectValue placeholder="Tipo" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="todos">Todos</SelectItem>
                                  <SelectItem value="cliente">
                                    <div className="flex items-center gap-2">
                                      <User size={12} className="text-blue-500" />
                                      Clientes
                                    </div>
                                  </SelectItem>
                                  <SelectItem value="proveedor">
                                    <div className="flex items-center gap-2">
                                      <Building2 size={12} className="text-orange-500" />
                                      Proveedores
                                    </div>
                                  </SelectItem>
                                </SelectContent>
                              </Select>
                              
                              {/* Filter by saldo */}
                              <Select
                                value={partyFilters.saldoTipo}
                                onValueChange={(value) => setPartyFilters(prev => ({ ...prev, saldoTipo: value }))}
                              >
                                <SelectTrigger className="w-[150px] h-8 text-sm" data-testid="party-filter-balance">
                                  <SelectValue placeholder="Saldo" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="todos">Todos los saldos</SelectItem>
                                  <SelectItem value="positivo">
                                    <div className="flex items-center gap-2">
                                      <TrendingUp size={12} className="text-green-500" />
                                      Saldo positivo
                                    </div>
                                  </SelectItem>
                                  <SelectItem value="negativo">
                                    <div className="flex items-center gap-2">
                                      <TrendingDown size={12} className="text-red-500" />
                                      Saldo negativo
                                    </div>
                                  </SelectItem>
                                </SelectContent>
                              </Select>
                              
                              {/* Clear filters button */}
                              {hasPartyFiltersActive() && (
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  className="h-8 text-xs text-blue-600 hover:text-blue-800 gap-1"
                                  onClick={resetPartyFilters}
                                  data-testid="party-filter-clear"
                                >
                                  <XIcon size={14} />
                                  Limpiar
                                </Button>
                              )}
                              
                              {/* Export button */}
                              <div className="ml-auto">
                                <Button 
                                  variant="outline" 
                                  size="sm" 
                                  className="h-8 text-xs gap-1"
                                  onClick={exportPartyReport}
                                  data-testid="party-export-btn"
                                >
                                  <Download size={14} />
                                  {hasPartyFiltersActive() ? 'Exportar Filtrado' : 'Exportar Terceros'}
                                </Button>
                              </div>
                            </div>
                          </TableCell>
                        </TableRow>

                        {/* Header + filas + TOTAL NETO comparten filteredParties */}
                        {(() => {
                          const partyData = processDataByParty(weeklyTotals);
                          const filteredParties = filterPartyData(partyData);
                          const sortedParties = [...filteredParties].sort((a, b) => {
                            const tot = (p) => Object.entries(p.weeks).filter(([i]) => columnVisible[+i]).reduce((s, [, w]) => s + Math.abs(w.ingresos - w.egresos), 0);
                            return tot(b) - tot(a);
                          });
                          const filteredWeekNet = (wi) => filteredParties.reduce((s, p) => {
                            const wd = p.weeks[wi] || { ingresos: 0, egresos: 0 };
                            return s + (wd.ingresos - wd.egresos);
                          }, 0);
                          const filteredGrandNet = weeklyTotals.reduce((s, _, i) => columnVisible[i] ? s + filteredWeekNet(i) : s, 0);

                          return (<>
                            {/* Header — totales de terceros filtrados */}
                            <TableRow className="bg-gray-100 font-bold border-b-2">
                              <TableCell className="sticky left-0 bg-gray-100">
                                <div className="flex items-center gap-2">
                                  <Building2 size={16} className="text-gray-600" />
                                  PROVEEDOR / CLIENTE
                                </div>
                              </TableCell>
                              {weeklyTotals.map((_, idx) => columnVisible[idx] ? (
                                <TableCell key={idx} className="text-center text-xs text-gray-600">
                                  Tot: {formatCurrency(filteredWeekNet(idx))}
                                </TableCell>
                              ) : null)}
                              <TableCell className="text-center bg-gray-200 font-bold">TOTAL</TableCell>
                            </TableRow>

                            {/* Filas de terceros */}
                            {sortedParties.length === 0 ? (
                              <TableRow>
                                <TableCell colSpan={weeklyTotals.length + 2} className="text-center py-8 text-gray-500">
                                  {hasPartyFiltersActive()
                                    ? 'No hay terceros que coincidan con los filtros seleccionados'
                                    : 'No hay datos de proveedores/clientes para mostrar'}
                                </TableCell>
                              </TableRow>
                            ) : sortedParties.map(party => {
                              const totalIngresos = Object.entries(party.weeks).filter(([wi]) => columnVisible[+wi]).reduce((s, [, w]) => s + w.ingresos, 0);
                              const totalEgresos  = Object.entries(party.weeks).filter(([wi]) => columnVisible[+wi]).reduce((s, [, w]) => s + w.egresos,  0);
                              const netTotal = totalIngresos - totalEgresos;
                              if (totalIngresos === 0 && totalEgresos === 0) return null;
                              return (
                                <TableRow key={party.id} className={`hover:bg-gray-50 ${party.tipo === 'cliente' ? 'hover:bg-green-50/30' : 'hover:bg-red-50/30'}`}>
                                  <TableCell className="sticky left-0 bg-white">
                                    <div className="flex items-center gap-2">
                                      {party.tipo === 'cliente' ? <User size={14} className="text-blue-500" /> : <Building2 size={14} className="text-orange-500" />}
                                      <span className="truncate max-w-[170px]" title={party.nombre}>{party.nombre}</span>
                                      <Badge variant="outline" className="text-xs ml-1">{party.tipo === 'cliente' ? 'C' : 'P'}</Badge>
                                    </div>
                                  </TableCell>
                                  {weeklyTotals.map((_, weekIdx) => {
                                    if (!columnVisible[weekIdx]) return null;
                                    const weekData = party.weeks[weekIdx] || { ingresos: 0, egresos: 0 };
                                    const netValue = weekData.ingresos - weekData.egresos;
                                    const hasItems = weekData.ingresos > 0 || weekData.egresos > 0;
                                    return (
                                      <TableCell key={weekIdx}
                                        className={`text-center text-sm ${netValue > 0 ? 'text-green-600' : netValue < 0 ? 'text-red-600' : 'text-gray-400'} ${hasItems ? 'cursor-pointer hover:bg-gray-100 hover:underline' : ''}`}
                                        onClick={() => {
                                          if (!hasItems) return;
                                          setDrillDownData({
                                            weekNum: weekIdx + 1,
                                            weekLabel: weeklyData[weekIdx]?.displayLabel || weeklyData[weekIdx]?.label || `S${weekIdx + 1}`,
                                            dateLabel: weeklyData[weekIdx]?.dateLabel || '',
                                            dataType: weeklyData[weekIdx]?.dataType,
                                            tipo: netValue >= 0 ? 'ingreso' : 'egreso',
                                            categoryName: party.nombre,
                                            subcategoryName: party.tipo === 'cliente' ? 'Cliente' : 'Proveedor',
                                            items: (weekData.items || []).map(item => ({ ...item, tercero: party.nombre, terceroTipo: party.tipo })),
                                            total: netValue
                                          });
                                          setDrillDownOpen(true);
                                        }}
                                      >
                                        {netValue !== 0 ? formatCurrency(netValue) : '-'}
                                      </TableCell>
                                    );
                                  })}
                                  <TableCell className={`text-center font-bold ${netTotal > 0 ? 'bg-green-50 text-green-700' : netTotal < 0 ? 'bg-red-50 text-red-700' : 'text-gray-500'}`}>
                                    {formatCurrency(netTotal)}
                                  </TableCell>
                                </TableRow>
                              );
                            })}

                            {/* TOTAL NETO — filtrado */}
                            <TableRow className="bg-gray-200 font-bold border-t-2">
                              <TableCell className="sticky left-0 bg-gray-200">TOTAL NETO</TableCell>
                              {weeklyTotals.map((_, idx) => columnVisible[idx] ? (
                                <TableCell key={idx} className={`text-center font-bold ${filteredWeekNet(idx) >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                                  {formatCurrency(filteredWeekNet(idx))}
                                </TableCell>
                              ) : null)}
                              <TableCell className={`text-center font-bold ${filteredGrandNet >= 0 ? 'text-green-800 bg-green-100' : 'text-red-800 bg-red-100'}`}>
                                {formatCurrency(filteredGrandNet)}
                              </TableCell>
                            </TableRow>
                          </>);
                        })()}
                      </>
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
          </div> {/* End of reportRef div for PDF export */}
        </TabsContent>

        {/* MONTHLY VIEW */}
        <TabsContent value="monthly" className="mt-4">
          <Card>
            <CardHeader className="bg-[#1B1740] text-white rounded-t-lg">
              <CardTitle>Proyección Mensual</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-100">
                      <TableHead className="sticky left-0 bg-gray-100 min-w-[250px] font-bold">CONCEPTO</TableHead>
                      {monthlyData.map((month, idx) => (
                        <TableHead key={idx} className={`text-center min-w-[130px] capitalize ${
                          month.isCurrent ? 'bg-yellow-50 text-yellow-800' :
                          month.isPast ? 'text-gray-700' : 'text-blue-700'
                        }`}>
                          {month.label}
                          {month.isCurrent && <span className="block text-xs font-normal text-yellow-600">Actual</span>}
                          {month.isPast && <span className="block text-xs font-normal text-gray-400">Real</span>}
                        </TableHead>
                      ))}
                      <TableHead className="text-center min-w-[130px] bg-blue-50 font-bold">TOTAL</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {/* INGRESOS */}
                    <TableRow className="bg-green-100 font-bold">
                      <TableCell className="sticky left-0 bg-green-100">
                        <TrendingUp className="inline mr-2 text-green-600" size={16} />
                        INGRESOS
                      </TableCell>
                      {monthlyData.map((month, idx) => (
                        <TableCell key={idx} className="text-center text-green-700">
                          {formatCurrency(month.ingresos.total)}
                        </TableCell>
                      ))}
                      <TableCell className="text-center bg-green-200 text-green-800">
                        {formatCurrency(monthlyData.reduce((s, m) => s + m.ingresos.total, 0))}
                      </TableCell>
                    </TableRow>
                    
                    {/* Ingresos by Category */}
                    {(() => {
                      const SIN_CLAS = ['Cobranza sin clasificar'];
                      const allCats = new Set();
                      monthlyData.forEach(m => Object.keys(m.ingresos.byCategory).forEach(c => allCats.add(c)));
                      return Array.from(allCats).map(catName => {
                        const monthTotals = monthlyData.map(m => m.ingresos.byCategory[catName] || 0);
                        const total = monthTotals.reduce((s, t) => s + t, 0);
                        if (total === 0) return null;
                        const isSinClas = SIN_CLAS.includes(catName);
                        return (
                          <TableRow key={`monthly-ing-${catName}`} className={isSinClas ? 'bg-amber-50 hover:bg-amber-100' : 'hover:bg-green-50'}>
                            <TableCell className={`sticky left-0 pl-8 ${isSinClas ? 'bg-amber-50 text-amber-700' : 'bg-white'}`}>
                              {isSinClas ? <span className="flex items-center gap-1"><AlertTriangle size={12} className="text-amber-500" />{catName}</span> : catName}
                            </TableCell>
                            {monthTotals.map((t, idx) => (
                              <TableCell key={idx} className={`text-center ${isSinClas ? 'text-amber-600' : 'text-green-600'}`}>
                                {t > 0 ? formatCurrency(t) : '-'}
                              </TableCell>
                            ))}
                            <TableCell className={`text-center ${isSinClas ? 'bg-amber-100 text-amber-700' : 'bg-green-50'}`}>{formatCurrency(total)}</TableCell>
                          </TableRow>
                        );
                      });
                    })()}

                    {/* EGRESOS */}
                    <TableRow className="bg-red-100 font-bold">
                      <TableCell className="sticky left-0 bg-red-100">
                        <TrendingDown className="inline mr-2 text-red-600" size={16} />
                        EGRESOS
                      </TableCell>
                      {monthlyData.map((month, idx) => (
                        <TableCell key={idx} className="text-center text-red-700">
                          {formatCurrency(month.egresos.total)}
                        </TableCell>
                      ))}
                      <TableCell className="text-center bg-red-200 text-red-800">
                        {formatCurrency(monthlyData.reduce((s, m) => s + m.egresos.total, 0))}
                      </TableCell>
                    </TableRow>
                    
                    {/* Egresos by Category */}
                    {(() => {
                      const SIN_CLAS = ['Pagos sin clasificar'];
                      const allCats = new Set();
                      monthlyData.forEach(m => Object.keys(m.egresos.byCategory).forEach(c => allCats.add(c)));
                      return Array.from(allCats).map(catName => {
                        const monthTotals = monthlyData.map(m => m.egresos.byCategory[catName] || 0);
                        const total = monthTotals.reduce((s, t) => s + t, 0);
                        if (total === 0) return null;
                        const isSinClas = SIN_CLAS.includes(catName);
                        return (
                          <TableRow key={`monthly-egr-${catName}`} className={isSinClas ? 'bg-amber-50 hover:bg-amber-100' : 'hover:bg-red-50'}>
                            <TableCell className={`sticky left-0 pl-8 ${isSinClas ? 'bg-amber-50 text-amber-700' : 'bg-white'}`}>
                              {isSinClas ? <span className="flex items-center gap-1"><AlertTriangle size={12} className="text-amber-500" />{catName}</span> : catName}
                            </TableCell>
                            {monthTotals.map((t, idx) => (
                              <TableCell key={idx} className={`text-center ${isSinClas ? 'text-amber-600' : 'text-red-600'}`}>
                                {t > 0 ? formatCurrency(t) : '-'}
                              </TableCell>
                            ))}
                            <TableCell className={`text-center ${isSinClas ? 'bg-amber-100 text-amber-700' : 'bg-red-50'}`}>{formatCurrency(total)}</TableCell>
                          </TableRow>
                        );
                      });
                    })()}

                    {/* TRASPASOS ENTRE CUENTAS — informativo */}
                    {(() => {
                      const totalTrasIng = monthlyData.reduce((s, m) => s + (m.traspasos_ingreso || 0), 0);
                      const totalTrasEgr = monthlyData.reduce((s, m) => s + (m.traspasos_egreso  || 0), 0);
                      if (totalTrasIng === 0 && totalTrasEgr === 0) return null;
                      return (
                        <TableRow className="bg-purple-50/60 border-t border-purple-200">
                          <TableCell className="sticky left-0 bg-purple-50/60 text-purple-700 text-sm">
                            <span className="flex items-center gap-1">↔ Traspasos entre cuentas <span className="text-xs text-purple-400">(informativo)</span></span>
                          </TableCell>
                          {monthlyData.map((month, idx) => {
                            const ing = month.traspasos_ingreso || 0;
                            const egr = month.traspasos_egreso  || 0;
                            if (ing === 0 && egr === 0) return <TableCell key={idx} className="text-center text-xs text-gray-400">-</TableCell>;
                            return (
                              <TableCell key={idx} className="text-center text-xs text-purple-600">
                                {ing > 0 && <div className="text-green-600">↑ {formatCurrency(ing)}</div>}
                                {egr > 0 && <div className="text-red-600">↓ {formatCurrency(egr)}</div>}
                              </TableCell>
                            );
                          })}
                          <TableCell className="text-center text-xs bg-purple-100 text-purple-700">
                            ↑{formatCurrency(totalTrasIng)}<br/>↓{formatCurrency(totalTrasEgr)}
                          </TableCell>
                        </TableRow>
                      );
                    })()}

                    {/* SIN CLASIFICAR — botón para clasificar desde vista mensual */}
                    {(() => {
                      const allSin = monthlyData.flatMap(m => m.sinClasificar || []);
                      if (allSin.length === 0) return null;
                      return (
                        <TableRow className="bg-amber-50 border-t-2 border-amber-300">
                          <TableCell className="sticky left-0 bg-amber-50 text-amber-700 font-semibold text-sm">
                            <span className="flex items-center gap-1">
                              <AlertTriangle size={13} className="text-amber-500" />
                              Sin clasificar ({allSin.length} mov.)
                              <span className="text-xs font-normal text-amber-500 ml-1">— haz clic en ✎ para clasificar</span>
                            </span>
                          </TableCell>
                          {monthlyData.map((month, idx) => {
                            const items = month.sinClasificar || [];
                            if (items.length === 0) return <TableCell key={idx} className="text-center text-xs text-gray-400">-</TableCell>;
                            return (
                              <TableCell key={idx} className="text-center text-xs">
                                {items.map((item, ii) => (
                                  <div key={ii} className="flex items-center justify-center gap-1 py-0.5">
                                    <span className={item.tipo === 'ingreso' ? 'text-green-600' : 'text-red-600'}>
                                      {formatCurrency(item.monto)}
                                    </span>
                                    <button
                                      className="text-amber-500 hover:text-amber-700 underline text-xs"
                                      onClick={() => setClasificarModal({ open: true, item, nuevaCategoria: '' })}
                                      title={item.concepto || item.nombre}
                                    >✎</button>
                                  </div>
                                ))}
                              </TableCell>
                            );
                          })}
                          <TableCell className="text-center bg-amber-100 text-amber-700 text-xs font-bold">
                            {allSin.length} mov. por clasificar
                          </TableCell>
                        </TableRow>
                      );
                    })()}

                    {/* SALDO INICIAL DEL MES */}
                    <TableRow className="bg-gray-800 text-white font-bold border-t-2 border-gray-600">
                      <TableCell className="sticky left-0 bg-gray-800 text-white">SALDO INICIAL MES</TableCell>
                      {monthlyData.map((month, idx) => (
                        <TableCell key={idx} className="text-center text-blue-300 font-bold">
                          {formatCurrency(month.saldoInicial ?? 0)}
                        </TableCell>
                      ))}
                      <TableCell className="text-center bg-gray-700 text-blue-200 font-bold">
                        {formatCurrency(monthlyData[0]?.saldoInicial ?? 0)}
                      </TableCell>
                    </TableRow>

                    {/* FLUJO NETO */}
                    <TableRow className="bg-blue-100 font-bold border-t-2">
                      <TableCell className="sticky left-0 bg-blue-100">FLUJO NETO</TableCell>
                      {monthlyData.map((month, idx) => {
                        const neto = month.ingresos.total - month.egresos.total;
                        return (
                          <TableCell key={idx} className={`text-center font-bold ${neto >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                            {formatCurrency(neto)}
                          </TableCell>
                        );
                      })}
                      <TableCell className="text-center bg-blue-200 font-bold">
                        {formatCurrency(monthlyData.reduce((s, m) => s + m.ingresos.total - m.egresos.total, 0))}
                      </TableCell>
                    </TableRow>

                    {/* SALDO FINAL DEL MES — debe cuadrar con estado de cuenta bancario */}
                    <TableRow className="bg-[#1B1740] text-white font-bold border-t-2">
                      <TableCell className="sticky left-0 bg-[#1B1740] text-white">SALDO FINAL MES</TableCell>
                      {monthlyData.map((month, idx) => (
                        <TableCell
                          key={idx}
                          className={`text-center font-bold text-sm ${(month.saldoFinal ?? 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}
                        >
                          {formatCurrency(month.saldoFinal ?? 0)}
                        </TableCell>
                      ))}
                      <TableCell className={`text-center font-bold ${(monthlyData[monthlyData.length - 1]?.saldoFinal ?? 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                        {formatCurrency(monthlyData[monthlyData.length - 1]?.saldoFinal ?? 0)}
                      </TableCell>
                    </TableRow>

                    {/* VARIACIÓN MES (SF − SI) — comprobación: debe = Flujo Neto */}
                    <TableRow className="bg-amber-50 font-medium text-xs">
                      <TableCell className="sticky left-0 bg-amber-50 text-amber-700 text-xs">
                        ∆ Variación Mes (SF − SI)
                      </TableCell>
                      {monthlyData.map((month, idx) => {
                        const delta = (month.saldoFinal ?? 0) - (month.saldoInicial ?? 0);
                        return (
                          <TableCell key={idx} className={`text-center text-xs ${delta >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {formatCurrency(delta)}
                          </TableCell>
                        );
                      })}
                      <TableCell className="text-center bg-amber-100 text-amber-700 text-xs font-bold">
                        {formatCurrency(
                          (monthlyData[monthlyData.length - 1]?.saldoFinal ?? 0) -
                          (monthlyData[0]?.saldoInicial ?? 0)
                        )}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* BY PARTY VIEW */}
        <TabsContent value="byParty" className="mt-4 space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Facturas por Cliente / Proveedor</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex gap-4 mb-4">
                <div className="w-48">
                  <Select value={selectedPartyType} onValueChange={(v) => { setSelectedPartyType(v); setSelectedParty(''); }}>
                    <SelectTrigger>
                      <SelectValue placeholder="Tipo" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Seleccionar tipo...</SelectItem>
                      <SelectItem value="customer">
                        <div className="flex items-center gap-2">
                          <User size={14} className="text-blue-500" />
                          Clientes
                        </div>
                      </SelectItem>
                      <SelectItem value="vendor">
                        <div className="flex items-center gap-2">
                          <Building2 size={14} className="text-orange-500" />
                          Proveedores
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {selectedPartyType !== 'all' && (
                  <div className="flex-1">
                    <Select value={selectedParty} onValueChange={setSelectedParty}>
                      <SelectTrigger>
                        <SelectValue placeholder={selectedPartyType === 'customer' ? 'Seleccionar cliente...' : 'Seleccionar proveedor...'} />
                      </SelectTrigger>
                      <SelectContent>
                        {(selectedPartyType === 'customer' ? paymentClientes : paymentProveedores).map(party => (
                          <SelectItem key={party.id} value={party.id}>
                            <div className="flex flex-col">
                              <span className="font-medium">{party.nombre}</span>
                              <span className="text-xs text-gray-500">{party.count} mov · {formatCurrency(party.total)}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>

              {/* Party CFDIs Table */}
              {selectedParty && (
                <div className="border rounded-lg">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50">
                        <TableHead>UUID</TableHead>
                        <TableHead>Fecha</TableHead>
                        <TableHead>Tipo</TableHead>
                        <TableHead>Categoría</TableHead>
                        <TableHead>Método Pago</TableHead>
                        <TableHead className="text-right">Subtotal</TableHead>
                        <TableHead className="text-right">IVA</TableHead>
                        <TableHead className="text-right">Total</TableHead>
                        <TableHead className="text-right">Pagado</TableHead>
                        <TableHead className="text-right">Pendiente</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {getPartyCfdis().length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={10} className="text-center py-8 text-gray-500">
                            No hay movimientos para este {selectedPartyType === 'customer' ? 'cliente' : 'proveedor'}
                          </TableCell>
                        </TableRow>
                      ) : (
                        getPartyCfdis().map(pago => {
                          const category = categories.find(c => c.code === pago.category_id || c.id === pago.category_id);
                          const isIngreso = pago.tipo === 'cobro';
                          
                          return (
                            <TableRow key={pago.id} className="hover:bg-gray-50">
                              <TableCell className="font-mono text-xs">{pago.cfdi_uuid?.substring(0, 8) || pago.id?.substring(0, 8)}...</TableCell>
                              <TableCell>{pago.fecha_pago ? format(new Date(pago.fecha_pago), 'dd/MM/yy') : '-'}</TableCell>
                              <TableCell>
                                <span className={`text-xs px-2 py-1 rounded ${isIngreso ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                  {isIngreso ? '↑ Cobro' : '↓ Pago'}
                                </span>
                              </TableCell>
                              <TableCell>{category?.nombre || pago.category_name || 'Sin categoría'}</TableCell>
                              <TableCell className="text-xs">{pago.moneda || 'MXN'}</TableCell>
                              <TableCell className="text-right font-mono">{formatCurrency(pago.monto)}</TableCell>
                              <TableCell className="text-right font-mono text-gray-500">-</TableCell>
                              <TableCell className="text-right font-mono font-bold">{formatCurrency(pago.monto)}</TableCell>
                              <TableCell className="text-right font-mono text-green-600">{formatCurrency(pago.monto)}</TableCell>
                              <TableCell className="text-right font-mono font-bold text-green-600">
                                {formatCurrency(0)}
                              </TableCell>
                            </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                  
                  {/* Summary */}
                  {getPartyCfdis().length > 0 && (
                    <div className="p-4 bg-gray-50 border-t">
                      <div className="flex justify-between items-center">
                        <div className="text-sm text-gray-600">
                          <FileText className="inline mr-2" size={14} />
                          {getPartyCfdis().length} movimiento(s)
                        </div>
                        <div className="flex gap-8">
                          <div className="text-right">
                            <div className="text-xs text-gray-500">Total Cobrado/Pagado</div>
                            <div className="font-bold">{formatCurrency(getPartyCfdis().reduce((s, p) => s + (p.monto || 0), 0))}</div>
                          </div>
                          <div className="text-right">
                            <div className="text-xs text-gray-500">Ingresos</div>
                            <div className="font-bold text-green-600">
                              {formatCurrency(getPartyCfdis().filter(p => p.tipo === 'cobro').reduce((s, p) => s + (p.monto || 0), 0))}
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-xs text-gray-500">Egresos</div>
                            <div className="font-bold text-red-600">
                              {formatCurrency(getPartyCfdis().filter(p => p.tipo === 'pago').reduce((s, p) => s + (p.monto || 0), 0))}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* ===== KPI INSIGHT MODAL ===== */}
      <Dialog open={kpiModal.open} onOpenChange={(o) => setKpiModal(prev => ({ ...prev, open: o }))}>
        <DialogContent className="max-w-xl" data-testid="kpi-insight-modal">
          <DialogHeader className="pb-3 border-b border-slate-100">
            <DialogTitle className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-2.5 text-[#0F172A]">
                {kpiModal.kpiKey === 'burnRate'    && <Activity     size={20} className="text-blue-600 flex-shrink-0" />}
                {kpiModal.kpiKey === 'cashGap'     && <AlertTriangle size={20} className={kpiModal.status === 'danger' ? 'text-red-600 flex-shrink-0' : 'text-amber-600 flex-shrink-0'} />}
                {kpiModal.kpiKey === 'volatilidad' && <BarChart3    size={20} className="text-purple-600 flex-shrink-0" />}
                {kpiModal.kpiKey === 'operativos'  && <Target       size={20} className="text-amber-600 flex-shrink-0" />}
                <span className="font-bold text-base leading-tight">{kpiModal.name}</span>
              </div>
              <span className={`flex-shrink-0 text-xs font-bold px-2.5 py-1 rounded-full ${
                kpiModal.status === 'good'    ? 'bg-green-100 text-green-700 ring-1 ring-green-200'  :
                kpiModal.status === 'warning' ? 'bg-amber-100 text-amber-700 ring-1 ring-amber-200' :
                kpiModal.status === 'danger'  ? 'bg-red-100   text-red-700   ring-1 ring-red-200'   :
                'bg-slate-100 text-slate-600 ring-1 ring-slate-200'
              }`}>
                {kpiModal.status === 'good' ? '● Favorable' : kpiModal.status === 'warning' ? '● Atención' : kpiModal.status === 'danger' ? '● Riesgo' : '● Neutral'}
              </span>
            </DialogTitle>
            {kpiModal.description && (
              <p className="text-xs text-slate-500 mt-1.5 leading-relaxed">{kpiModal.description}</p>
            )}
          </DialogHeader>

          <div className="space-y-4 mt-3 max-h-[65vh] overflow-y-auto pr-0.5">
            {/* Fórmula */}
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
              <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1.5">Fórmula</div>
              <code className="text-xs text-slate-700 leading-relaxed whitespace-pre-wrap font-mono">{kpiModal.formula}</code>
            </div>

            {/* Valores que entran al cálculo */}
            <div>
              <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-2">Valores que entran al cálculo</div>
              <div className="divide-y divide-slate-100 border border-slate-100 rounded-lg overflow-hidden">
                {Object.entries(
                  kpiModal.kpiKey === 'cashGap' ? {
                    'Umbral mínimo de caja': '__UMBRAL_EDITABLE__',
                    'Semanas en riesgo (en rango visible)': String(cfoKPIs.semanasEnRiesgo.length),
                    'Semana con saldo más bajo': cfoKPIs.semanaCritica?.semana || 'N/A',
                    'Saldo en semana crítica': formatCurrency(cfoKPIs.semanaCritica?.saldoFinal || 0),
                  } : kpiModal.values
                ).map(([k, v]) => (
                  <div key={k} className="flex justify-between items-center px-3 py-2 bg-white hover:bg-slate-50 transition-colors">
                    <span className="text-xs text-slate-500 mr-4">{k}</span>
                    {v === '__UMBRAL_EDITABLE__' ? (
                      <Input
                        type="number"
                        className="h-7 w-36 text-xs font-semibold text-right"
                        value={umbralMinimoCaja}
                        onChange={(e) => updateUmbralMinimoCaja(e.target.value)}
                        data-testid="cash-gap-umbral-input"
                      />
                    ) : (
                      <span className="text-xs font-semibold text-slate-800 text-right">{v}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Análisis AI */}
            <div className={`rounded-lg p-4 border ${
              kpiModal.status === 'good'    ? 'bg-gradient-to-br from-green-50  to-emerald-50 border-green-100'  :
              kpiModal.status === 'danger'  ? 'bg-gradient-to-br from-red-50    to-rose-50    border-red-100'    :
              kpiModal.status === 'warning' ? 'bg-gradient-to-br from-amber-50  to-yellow-50  border-amber-100'  :
              'bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-100'
            }`}>
              <div className="flex items-center gap-2 mb-2.5">
                <div className="w-5 h-5 rounded-full bg-[#1B1740] flex items-center justify-center text-white text-[9px] font-bold tracking-tight flex-shrink-0">AI</div>
                <span className="text-[10px] font-semibold text-slate-600 uppercase tracking-wider">Análisis CFO · TAXNFIN</span>
              </div>
              {kpiModal.loading ? (
                <div className="flex items-center gap-2.5 text-sm text-slate-500 py-1">
                  <div className="w-4 h-4 border-2 border-slate-400 border-t-transparent rounded-full animate-spin flex-shrink-0" />
                  <span>Analizando el contexto del negocio...</span>
                </div>
              ) : (
                <p className="text-sm text-slate-700 leading-relaxed">{kpiModal.insight}</p>
              )}
            </div>
          </div>

          <DialogFooter className="border-t border-slate-100 pt-3 mt-1">
            <Button variant="outline" size="sm" onClick={() => setKpiModal(prev => ({ ...prev, open: false }))}>
              Cerrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===== DRILL-DOWN DIALOG ===== */}
      <Dialog open={drillDownOpen} onOpenChange={setDrillDownOpen}>
        <DialogContent className="max-w-5xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <Eye size={20} className={drillDownData.tipo === 'ingreso' ? 'text-green-600' : 'text-red-600'} />
              <span>
                Detalle: {drillDownData.categoryName}
                {drillDownData.subcategoryName && ` > ${drillDownData.subcategoryName}`}
              </span>
              <Badge variant={drillDownData.dataType === 'real' ? 'default' : drillDownData.dataType === 'actual' ? 'secondary' : 'outline'}>
                {drillDownData.weekLabel} - {drillDownData.dataType === 'real' ? 'Real' : drillDownData.dataType === 'actual' ? 'Actual' : 'Proyectado'}
              </Badge>
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-auto">
            {/* Summary */}
            <div className="grid grid-cols-3 gap-4 mb-4 p-4 bg-gray-50 rounded-lg">
              <div>
                <div className="text-xs text-gray-500">Semana</div>
                <div className="font-bold">{drillDownData.weekLabel} ({drillDownData.dateLabel})</div>
              </div>
              <div>
                <div className="text-xs text-gray-500">Tipo</div>
                <div className={`font-bold ${drillDownData.tipo === 'ingreso' ? 'text-green-600' : 'text-red-600'}`}>
                  {drillDownData.tipo === 'ingreso' ? '↑ Ingreso' : '↓ Egreso'}
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-500">Total</div>
                <div className="font-bold text-lg">{formatCurrency(drillDownData.total)}</div>
              </div>
            </div>

            {/* Items Table */}
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-100">
                  <TableHead className="w-[180px]">Proveedor/Cliente</TableHead>
                  <TableHead className="w-[100px]">UUID</TableHead>
                  <TableHead className="w-[90px]">Fecha</TableHead>
                  <TableHead className="text-right w-[100px]">Monto</TableHead>
                  <TableHead className="w-[70px]">Moneda</TableHead>
                  <TableHead className="text-right w-[110px]">Monto MXN</TableHead>
                  <TableHead className="w-[180px]">Mov. Bancario</TableHead>
                  <TableHead className="w-[90px] text-center">Conciliado</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {drillDownData.items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-gray-500">
                      No hay items para mostrar
                    </TableCell>
                  </TableRow>
                ) : (
                  drillDownData.items.map((item, idx) => (
                    <TableRow key={idx} className="hover:bg-gray-50">
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {item.terceroTipo === 'cliente' ? (
                            <User size={14} className="text-blue-500" />
                          ) : (
                            <Building2 size={14} className="text-orange-500" />
                          )}
                          <span className="truncate max-w-[150px]" title={item.tercero}>
                            {item.tercero || 'Sin asignar'}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="font-mono text-xs">
                        {item.uuid ? (
                          <span title={item.uuid}>{item.uuid.substring(0, 8)}...</span>
                        ) : '-'}
                      </TableCell>
                      <TableCell className="text-xs">
                        {item.fechaFactura ? format(new Date(item.fechaFactura), 'dd/MM/yy') : '-'}
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm">
                        {formatCurrency(item.montoOriginal || item.monto)}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs">
                          {item.moneda || 'MXN'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm font-bold">
                        {formatCurrency(item.monto)}
                      </TableCell>
                      <TableCell className="text-xs">
                        {item.bankTxnDescripcion ? (
                          <span className="truncate max-w-[170px] block" title={item.bankTxnDescripcion}>
                            {item.bankTxnDescripcion}
                          </span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        {item.conciliado ? (
                          <span className="inline-flex items-center justify-center w-6 h-6 bg-green-100 rounded-full">
                            <Check size={14} className="text-green-600" />
                          </span>
                        ) : (
                          <span className="inline-flex items-center justify-center w-6 h-6 bg-gray-100 rounded-full">
                            <XIcon size={14} className="text-gray-400" />
                          </span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
          
          <DialogFooter className="border-t pt-4">
            <div className="flex justify-between items-center w-full">
              <div className="text-sm text-gray-500">
                {drillDownData.items.length} movimiento(s)
              </div>
              <Button variant="outline" onClick={() => setDrillDownOpen(false)}>
                Cerrar
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ===== MODAL RECLASIFICAR MOVIMIENTO SIN CATEGORÍA ===== */}
      <Dialog open={clasificarModal.open} onOpenChange={(o) => !o && setClasificarModal({ open: false, item: null, nuevaCategoria: '' })}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="text-amber-500">⚠️</span> Clasificar Movimiento
            </DialogTitle>
          </DialogHeader>
          {clasificarModal.item && (
            <div className="space-y-4 py-2">
              <div className="bg-gray-50 rounded-lg p-3 text-sm space-y-1">
                <div className="flex justify-between">
                  <span className="text-gray-500">Concepto:</span>
                  <span className="font-medium">{clasificarModal.item.concepto || clasificarModal.item.nombre || '—'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Monto:</span>
                  <span className="font-bold">{formatCurrency(clasificarModal.item.monto)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Fecha:</span>
                  <span>{clasificarModal.item.fecha || '—'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Categoría actual:</span>
                  <span className="text-amber-600">{clasificarModal.item.categoria || 'Sin clasificar'}</span>
                </div>
              </div>
              <div>
                <Label className="text-sm font-medium mb-1 block">Nueva categoría</Label>
                <Select
                  value={clasificarModal.nuevaCategoria}
                  onValueChange={(v) => setClasificarModal(prev => ({ ...prev, nuevaCategoria: v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecciona una categoría..." />
                  </SelectTrigger>
                  <SelectContent className="max-h-80">
                    {/* Ingresos */}
                    {categories.filter(c => c.tipo === 'ingreso').length > 0 && (
                      <>
                        <SelectItem value="__header_ing__" disabled className="text-xs text-green-700 font-bold bg-green-50 cursor-default">▲ INGRESOS</SelectItem>
                        {categories.filter(c => c.tipo === 'ingreso').map(cat => (
                          <SelectItem key={cat.code || cat.nombre} value={cat.nombre} className="pl-4">
                            {cat.nombre}
                          </SelectItem>
                        ))}
                      </>
                    )}
                    {/* Egresos */}
                    {categories.filter(c => c.tipo === 'egreso').length > 0 && (
                      <>
                        <SelectItem value="__header_egr__" disabled className="text-xs text-red-700 font-bold bg-red-50 cursor-default">▼ EGRESOS</SelectItem>
                        {categories.filter(c => c.tipo === 'egreso').map(cat => (
                          <SelectItem key={cat.code || cat.nombre} value={cat.nombre} className="pl-4">
                            {cat.nombre}
                          </SelectItem>
                        ))}
                      </>
                    )}
                    {/* Interno */}
                    <SelectItem value="__header_int__" disabled className="text-xs text-purple-700 font-bold bg-purple-50 cursor-default">↔ INTERNO</SelectItem>
                    <SelectItem value="Traspaso entre cuentas" className="pl-4">Traspaso entre cuentas</SelectItem>
                    <SelectItem value="Comisiones bancarias" className="pl-4">Comisiones bancarias</SelectItem>
                  </SelectContent>
                </Select>
                {/* Campo libre si la categoría no está en el catálogo */}
                <div className="mt-2">
                  <Label className="text-xs text-gray-500 mb-1 block">O escribe una categoría personalizada:</Label>
                  <Input
                    placeholder="Ej. Importaciones, ISN, Arrendamiento..."
                    value={clasificarModal.nuevaCategoria.startsWith('__custom__')
                      ? clasificarModal.nuevaCategoria.replace('__custom__', '')
                      : (!categories.some(c => c.nombre === clasificarModal.nuevaCategoria) && clasificarModal.nuevaCategoria
                          ? clasificarModal.nuevaCategoria : '')}
                    onChange={(e) => {
                      const v = e.target.value;
                      setClasificarModal(prev => ({ ...prev, nuevaCategoria: v }));
                    }}
                    className="text-sm h-8"
                  />
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setClasificarModal({ open: false, item: null, nuevaCategoria: '' })}>
              Cancelar
            </Button>
            <Button
              onClick={handleCategorizar}
              disabled={!clasificarModal.nuevaCategoria}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              Guardar clasificación
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CashflowProjections;
